import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  writeBatch,
  serverTimestamp,
  Timestamp,
  deleteField,
  QueryDocumentSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { db } from './firebaseService';
import { Category, CategoryCreateData, CategoryUpdateData, CategoryFilterOptions } from '@/types/categories';
import { StoreToAccountBookConverter } from '@/types/accounts';
import { Merchant, MerchantFormData } from '@/types/merchant';
import { formatLocalDateString } from '../utils/dateUtils';
import { mapEmployeeFromFirestore, normalizeEmployeeForFirestore } from '../utils/employeeMapper';
import { mapWorkScheduleFromFirestore } from '../utils/workScheduleMapper';
import type { WorkSchedule } from '../types/employee';
import { mergeAccessibleBooks, AccessibleBook, RawBook } from '@/utils/merchantAccess';

// Firestore 컬렉션 이름 상수 (Flutter 앱과 일치)
export const COLLECTIONS = {
  USERS: 'users',
  SHARED_BOOKS: 'sharedBooks',
  STORES: 'stores',
  EXPENSES: 'expenses',
  REPEAT_EXPENSES: 'repeat_expenses',
  MERCHANTS: 'merchant',        // Flutter 앱과 일치
  EMPLOYEES: 'employee',        // Flutter 앱과 일치
  WORK_SCHEDULES: 'work_schedule', // Flutter 앱과 일치
  WORK_TIME_TEMPLATES: 'workTimeTemplates', // 근무 타임 템플릿
  CATEGORIES: 'user_category',  // Flutter 앱과 일치
  TODO: 'todo',                 // Flutter 앱에 있음
  HISTORY: 'history',           // Flutter 앱에 있음
  HOURLY_RATE_HISTORY: 'hourly_rate_history' // 시급 변경 이력
} as const;

// 기본 문서 타입
export interface BaseDocument {
  id: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  ownerId: string;
}

// 사용자 문서 타입
export interface UserDocument extends BaseDocument {
  email: string;
  displayName: string;
  photoURL?: string;
  accountBookIds: string[];
  preferences: {
    defaultCurrency: string;
    dateFormat: string;
    timezone: string;
  };
}

// 거래 내역 문서 타입 (실제 Firestore 구조에 맞게)
export interface RecordDocument extends BaseDocument {
  // Flutter 앱의 실제 Firebase 구조에 맞게 수정
  name: string;                    // Flutter의 name
  incomeType: string;              // Flutter의 incomeType ('income' | 'expense')
  createdTime: Timestamp;          // Flutter의 createdTime
  money: number;                   // Flutter의 money
  content?: string;                // Flutter의 content
  merchantName?: string;           // Flutter의 merchantName
  accountNumber: number;           // Flutter의 accountNumber
  supplyPrice?: number;            // Flutter의 supplyPrice
  vatAmount?: number;              // Flutter의 vatAmount
  taxType?: string;                // Flutter의 taxType
  
  // 기존 필드들 (호환성을 위해 유지)
  type?: 'income' | 'expense';     // incomeType과 매핑
  amount?: number;                 // money와 매핑
  category?: string;               // name에서 추출
  description?: string;            // name과 매핑
  date?: Timestamp;                // createdTime과 매핑
  createTimeMilli?: number;        // createdTime에서 추출
  taxStatus?: 'taxable' | 'tax-free'; // taxType과 매핑
}

// ExpenseDocument는 RecordDocument와 동일하므로 별도 정의 불필요

// 매장(가계부) 문서 타입 (Flutter 앱과 일치하도록 수정)
export interface StoreDocument {
  // Firestore 문서 ID (문서 ID가 숫자로 저장됨)
  id: number;
  
  // Flutter 앱과 일치하는 필드들
  businessName: string; // "자람"
  address?: string; // "화도면 장화리 194"
  businessNumber?: string; // 사업자등록번호
  ownerPhone?: string; // "010-9406-4315"
  storePhone?: string; // "010-9406-4315"
  openingTime?: string; // "2000-01-01T14:03:00.000"
  closingTime?: string; // "2000-01-01T21:46:00.000"
  holidays?: string[]; // ["토", "일"]
  investment?: number; // 2000000
  loan?: number; // 0
  storeType?: string; // Flutter 앱에서 사용하는 필드
  
  // BaseDocument 필드들 (수동으로 추가)
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  ownerId?: string;
  
  // 기존 필드들 (호환성을 위해 유지)
  name?: string; // businessName과 매핑
  description?: string;
  accountNumber: number; // id와 매핑 (필수: 문서에 따르면 id는 1-5, accountNumber와 일치해야 함)
  isActive?: boolean;
  settings?: {
    timezone: string;
    fiscalYearStart: Timestamp;
    taxRate: number;
  };
}

// 반복 거래 실제 스키마 (sharedBooks/{uid}/repeat_expenses — Flutter 1:1, snake_case 유지)
export interface RepeatExpenseDocument {
  id: string;                   // uuid (문서 ID와 동일)
  name: string;                 // 카테고리
  incomeType: string;           // '매출' | '지출'
  money: number;
  merchantName?: string;
  content?: string;
  accountNumber: number;
  createTimeMilli: number;      // epoch ms
  supply_price?: number;        // 공급가액 (Flutter snake_case)
  vat_amount?: number;          // 부가세 (Flutter snake_case)
  tax_type?: string;            // 'taxable' | 'nonTaxable'
  isPlaceholder?: boolean;      // Flutter 가 빈 컬렉션 유지용으로 쓰는 시스템 문서
}

// (구) 반복 거래 문서 타입 — users/{uid} 경로 레거시
export interface RepeatRecordDocument extends BaseDocument {
  accountBookId: string;
  name: string;
  type: 'income' | 'expense';
  amount: number;
  category: string;
  merchantName?: string;
  description: string;
  taxStatus: 'taxable' | 'tax-free';
  isActive: boolean;
  repeatType: 'monthly' | 'weekly' | 'daily';
  repeatDay?: number; // 월반복의 경우 일자 (1-31)
  repeatWeekday?: number; // 주반복의 경우 요일 (0-6)
  lastExecuted?: Timestamp;
  nextExecution?: Timestamp;
}

// 공유 가계부 문서 타입
export interface SharedBookDocument extends BaseDocument {
  bookName: string;
  description?: string;
  hostId: string;
  hostName: string;
  hostEmail: string;
  members?: string[]; // 향후 확장을 위해 유지
  isActive: boolean;
  settings?: {
    timezone: string;
    fiscalYearStart: Timestamp;
    taxRate: number;
  };
}

// 쿼리 옵션 타입
export interface QueryOptions {
  limitCount?: number;
  limit?: number;
  orderByField?: string;
  orderDirection?: 'asc' | 'desc';
  startAfterDoc?: QueryDocumentSnapshot;
  whereConditions?: Array<{
    field: string;
    operator: '==' | '!=' | '<' | '<=' | '>' | '>=' | 'in' | 'not-in' | 'array-contains' | 'array-contains-any';
    value: any;
  }>;
}

// Firestore 서비스 클래스
class FirestoreService {
  // 컬렉션 참조 가져오기
  private getCollectionRef(collectionName: string) {
    return collection(db, collectionName);
  }

  // 문서 참조 가져오기
  private getDocRef(collectionName: string, docId: string) {
    return doc(db, collectionName, docId);
  }

  // 문서 추가
  async addDocument<T extends Omit<BaseDocument, 'id' | 'createdAt' | 'updatedAt'>>(
    collectionName: string,
    data: T
  ): Promise<string> {
    try {
      
      const collectionRef = this.getCollectionRef(collectionName);
      
      const docRef = await addDoc(collectionRef, {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      
      return docRef.id;
    } catch (error) {
      console.error(`❌ 문서 추가 실패: ${collectionName}`, error);
      throw error;
    }
  }

  // 문서 조회
  async getDocument<T extends BaseDocument>(
    collectionName: string,
    docId: string
  ): Promise<T | null> {
    try {
      const docRef = this.getDocRef(collectionName, docId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as T;
      } else {
        return null;
      }
    } catch (error) {
      console.error(`❌ 문서 조회 실패: ${collectionName}/${docId}`, error);
      throw error;
    }
  }

  // 문서 업데이트
  async updateDocument(
    collectionName: string,
    docId: string,
    data: Partial<BaseDocument>
  ): Promise<void> {
    try {
      const docRef = this.getDocRef(collectionName, docId);
      await updateDoc(docRef, {
        ...data,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error(`❌ 문서 업데이트 실패: ${collectionName}/${docId}`, error);
      throw error;
    }
  }

  // 문서 삭제
  async deleteDocument(collectionName: string, docId: string): Promise<void> {
    try {
      const docRef = this.getDocRef(collectionName, docId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error(`❌ 문서 삭제 실패: ${collectionName}/${docId}`, error);
      throw error;
    }
  }

  // 컬렉션 조회 (쿼리 옵션 포함)
  async getCollection<T extends BaseDocument>(
    collectionName: string,
    options?: QueryOptions
  ): Promise<T[]> {
    try {
      let q = query(this.getCollectionRef(collectionName));

      // Where 조건 추가
      if (options?.whereConditions) {
        options.whereConditions.forEach(condition => {
          q = query(q, where(condition.field, condition.operator, condition.value));
        });
      }

      // 정렬 추가
      if (options?.orderByField) {
        q = query(q, orderBy(options.orderByField, options.orderDirection || 'desc'));
      }

      // 페이지네이션
      if (options?.startAfterDoc) {
        q = query(q, startAfter(options.startAfterDoc));
      }

      // 제한
      if (options?.limitCount) {
        q = query(q, limit(options.limitCount));
      }

      const querySnapshot = await getDocs(q);
      const documents: T[] = [];

      querySnapshot.forEach((doc) => {
        documents.push({ id: doc.id, ...doc.data() } as T);
      });

      return documents;
    } catch (error) {
      console.error(`❌ 컬렉션 조회 실패: ${collectionName}`, error);
      throw error;
    }
  }

  // 실시간 리스너 설정
  onCollectionSnapshot<T extends BaseDocument>(
    collectionName: string,
    callback: (documents: T[]) => void,
    options?: QueryOptions
  ): Unsubscribe {
    let q = query(this.getCollectionRef(collectionName));

    // Where 조건 추가
    if (options?.whereConditions) {
      options.whereConditions.forEach(condition => {
        q = query(q, where(condition.field, condition.operator, condition.value));
      });
    }

    // 정렬 추가
    if (options?.orderByField) {
      q = query(q, orderBy(options.orderByField, options.orderDirection || 'desc'));
    }

    // 제한
    if (options?.limitCount) {
      q = query(q, limit(options.limitCount));
    }

    return onSnapshot(q, (querySnapshot) => {
      const documents: T[] = [];
      querySnapshot.forEach((doc) => {
        documents.push({ id: doc.id, ...doc.data() } as T);
      });
      callback(documents);
    });
  }

  // 배치 작업
  async batchWrite(operations: Array<{
    type: 'add' | 'update' | 'delete';
    collectionName: string;
    docId?: string;
    data?: any;
  }>): Promise<void> {
    try {
      const batch = writeBatch(db);

      operations.forEach(operation => {
        switch (operation.type) {
          case 'add':
            if (operation.data) {
              const docRef = doc(this.getCollectionRef(operation.collectionName));
              batch.set(docRef, {
                ...operation.data,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
              });
            }
            break;
          case 'update':
            if (operation.docId && operation.data) {
              const docRef = this.getDocRef(operation.collectionName, operation.docId);
              batch.update(docRef, {
                ...operation.data,
                updatedAt: serverTimestamp()
              });
            }
            break;
          case 'delete':
            if (operation.docId) {
              const docRef = this.getDocRef(operation.collectionName, operation.docId);
              batch.delete(docRef);
            }
            break;
        }
      });

      await batch.commit();
    } catch (error) {
      console.error('❌ 배치 작업 실패:', error);
      throw error;
    }
  }

  // 중첩된 컬렉션을 위한 메서드들
  private getSubCollectionRef(parentCollection: string, parentDocId: string, subCollection: string) {
    return collection(db, parentCollection, parentDocId, subCollection);
  }

  private getSubDocRef(parentCollection: string, parentDocId: string, subCollection: string, subDocId: string) {
    return doc(db, parentCollection, parentDocId, subCollection, subDocId);
  }

  // 중첩된 컬렉션에 문서 추가
  async addSubDocument<T extends Omit<BaseDocument, 'id' | 'createdAt' | 'updatedAt'>>(
    parentCollection: string,
    parentDocId: string,
    subCollection: string,
    data: T
  ): Promise<string> {
    try {
      const docRef = await addDoc(this.getSubCollectionRef(parentCollection, parentDocId, subCollection), {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error(`❌ 하위 문서 추가 실패: ${parentCollection}/${parentDocId}/${subCollection}`, error);
      throw error;
    }
  }

  // 중첩된 컬렉션에서 문서 조회
  async getSubDocument<T extends BaseDocument>(
    parentCollection: string,
    parentDocId: string,
    subCollection: string,
    subDocId: string
  ): Promise<T | null> {
    try {
      const docRef = this.getSubDocRef(parentCollection, parentDocId, subCollection, subDocId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as T;
      } else {
        return null;
      }
    } catch (error) {
      console.error(`❌ 하위 문서 조회 실패: ${parentCollection}/${parentDocId}/${subCollection}/${subDocId}`, error);
      throw error;
    }
  }

  // 중첩된 컬렉션 조회
  async getSubCollection<T extends BaseDocument>(
    parentCollection: string,
    parentDocId: string,
    subCollection: string,
    options?: QueryOptions
  ): Promise<T[]> {
    try {
      let q = query(this.getSubCollectionRef(parentCollection, parentDocId, subCollection));

      // Where 조건 추가
      if (options?.whereConditions) {
        options.whereConditions.forEach(condition => {
          q = query(q, where(condition.field, condition.operator, condition.value));
        });
      }

      // 정렬 추가
      if (options?.orderByField) {
        q = query(q, orderBy(options.orderByField, options.orderDirection || 'desc'));
      }

      // 페이지네이션
      if (options?.startAfterDoc) {
        q = query(q, startAfter(options.startAfterDoc));
      }

      // 제한
      if (options?.limitCount) {
        q = query(q, limit(options.limitCount));
      }

      const querySnapshot = await getDocs(q);
      const documents: T[] = [];

      querySnapshot.forEach((doc) => {
        documents.push({ id: doc.id, ...doc.data() } as T);
      });

      return documents;
    } catch (error) {
      console.error(`❌ 하위 컬렉션 조회 실패: ${parentCollection}/${parentDocId}/${subCollection}`, error);
      throw error;
    }
  }

  // 거래 내역 관련 메서드들 (새로운 구조: users/{userId}/expenses)
  async getExpensesByUser(userId: string, options?: QueryOptions): Promise<RecordDocument[]> {
    return this.getCollection<RecordDocument>(`users/${userId}/expenses`, options);
  }

  async getExpensesByDateRange(
    userId: string,
    startDate: Date,
    endDate: Date,
    options?: QueryOptions
  ): Promise<RecordDocument[]> {
    return this.getCollection<RecordDocument>(`users/${userId}/expenses`, {
      ...options,
      whereConditions: [
        { field: 'date', operator: '>=', value: Timestamp.fromDate(startDate) },
        { field: 'date', operator: '<=', value: Timestamp.fromDate(endDate) }
      ]
    });
  }

  async getRepeatExpensesByUser(userId: string): Promise<RepeatRecordDocument[]> {
    return this.getCollection<RepeatRecordDocument>(`users/${userId}/repeat_expenses`, {
      whereConditions: [
        { field: 'isActive', operator: '==', value: true }
      ]
    });
  }

  // ── 반복 거래 (sharedBooks/{userId}/repeat_expenses — Flutter 스키마 1:1) ──
  // 실제 도큐먼트는 snake_case 필드(supply_price/vat_amount/tax_type)와
  // createTimeMilli(integer)를 사용하며, 주기/활성화 필드는 없다 (수동 실행 템플릿).

  async getRepeatExpensesFromShared(userId: string): Promise<RepeatExpenseDocument[]> {
    const ref = collection(db, COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.REPEAT_EXPENSES);
    const snapshot = await getDocs(ref);
    return snapshot.docs
      .map((d) => ({ ...(d.data() as Omit<RepeatExpenseDocument, 'id'>), id: d.id }))
      .filter((r) => !r.isPlaceholder && r.incomeType !== 'placeholder');
  }

  async addRepeatExpenseToShared(
    userId: string,
    data: Omit<RepeatExpenseDocument, 'id'>
  ): Promise<string> {
    // Flutter 와 동일하게 uuid 를 문서 ID 이자 id 필드로 사용
    const id =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const ref = doc(db, COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.REPEAT_EXPENSES, id);
    await setDoc(ref, { ...data, id });
    return id;
  }

  async updateRepeatExpenseInShared(
    userId: string,
    id: string,
    data: Partial<Omit<RepeatExpenseDocument, 'id'>>
  ): Promise<void> {
    const ref = doc(db, COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.REPEAT_EXPENSES, id);
    await updateDoc(ref, data);
  }

  async deleteRepeatExpenseFromShared(userId: string, id: string): Promise<void> {
    const ref = doc(db, COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.REPEAT_EXPENSES, id);
    await deleteDoc(ref);
  }

  // 사용자 관련 메서드들 (새로운 구조: users/{userId}/stores)
  async getUserStores(userId: string): Promise<StoreToAccountBookConverter[]> {
    try {
      const storesRef = collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.STORES);
      const querySnapshot = await getDocs(storesRef);
      const stores: StoreToAccountBookConverter[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        stores.push({
          id: parseInt(doc.id) || 0, // 문서 ID를 숫자로 변환 (Flutter와 일치)
          businessName: data.businessName, // businessName이 실제로 저장되어 있음
          name: data.name || `${data.accountNumber || 1}호점`,
          description: data.description,
          ownerId: data.ownerId,
          accountNumber: data.accountNumber,
          isActive: data.isActive,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          settings: data.settings
        });
      });

      return stores;
    } catch (error) {
      console.error(`❌ 하위 컬렉션 조회 실패: users/${userId}/stores`, error);
      throw error;
    }
  }

  // 실제 사용할 수 있는 메서드들 (새로운 구조)
  async addExpense(userId: string, expenseData: Omit<RecordDocument, 'id' | 'createdAt' | 'updatedAt'>, isPremium: boolean = false): Promise<string> {
    try {
      // 프리미엄 사용자는 sharedBooks에, 일반 사용자는 users에 거래 내역 추가
      const collectionPath = isPremium 
        ? `sharedBooks/${userId}/expenses`
        : `users/${userId}/expenses`;
      
      
      const expenseId = await this.addDocument(collectionPath, expenseData);
      
      
      return expenseId;
    } catch (error: any) {
      console.error('❌ addExpense 실패:', {
        error: error.message,
        userId,
        isPremium,
        collectionPath: isPremium ? `sharedBooks/${userId}/expenses` : `users/${userId}/expenses`
      });
      throw error;
    }
  }

  async updateExpense(userId: string, expenseId: string, updateData: Partial<RecordDocument>, isPremium: boolean = false): Promise<void> {
    // 프리미엄 사용자는 sharedBooks에서, 일반 사용자는 users에서 거래 내역 업데이트
    const docPath = isPremium 
      ? `sharedBooks/${userId}/expenses/${expenseId}`
      : `users/${userId}/expenses/${expenseId}`;
    
    try {
      const docRef = doc(db, docPath);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error(`❌ 거래 내역 업데이트 실패: ${docPath}`, error);
      throw error;
    }
  }

  async deleteExpense(userId: string, expenseId: string, isPremium: boolean = false): Promise<void> {
    // 프리미엄 사용자는 sharedBooks에서, 일반 사용자는 users에서 거래 내역 삭제
    const docPath = isPremium 
      ? `sharedBooks/${userId}/expenses/${expenseId}`
      : `users/${userId}/expenses/${expenseId}`;
    
    try {
      const docRef = doc(db, docPath);
      await deleteDoc(docRef);
    } catch (error) {
      console.error(`❌ 거래 내역 삭제 실패: ${docPath}`, error);
      throw error;
    }
  }

  async getExpense(userId: string, expenseId: string, isPremium: boolean = false): Promise<RecordDocument | null> {
    try {
      // 프리미엄 사용자는 sharedBooks에서, 일반 사용자는 users에서 거래 내역 조회
      const docPath = isPremium 
        ? `sharedBooks/${userId}/expenses`
        : `users/${userId}/expenses`;
      
      return await this.getDocument<RecordDocument>(docPath, expenseId);
    } catch (error) {
      console.error('❌ getExpense 실패:', error);
      throw error;
    }
  }

  // 실시간 리스너 (새로운 구조: users/{userId}/expenses 또는 sharedBooks/{userId}/expenses)
  onExpensesSnapshot(
    userId: string,
    callback: (expenses: RecordDocument[]) => void,
    options?: QueryOptions
  ): Unsubscribe {
    // 사용자 권한 확인 (비동기이므로 실시간 리스너에서는 기본적으로 users 사용)
    // 프리미엄 사용자의 경우 별도의 onSharedExpensesSnapshot 사용 권장
    const collectionPath = `users/${userId}/expenses`;
    let q = query(collection(db, collectionPath));

    // Where 조건 추가
    if (options?.whereConditions) {
      options.whereConditions.forEach(condition => {
        q = query(q, where(condition.field, condition.operator, condition.value));
      });
    }

    // 정렬 추가
    if (options?.orderByField) {
      q = query(q, orderBy(options.orderByField, options.orderDirection || 'desc'));
    }

    // 제한
    if (options?.limitCount) {
      q = query(q, limit(options.limitCount));
    }

    return onSnapshot(q, (querySnapshot) => {
      const expenses: RecordDocument[] = [];
      querySnapshot.forEach((doc) => {
        expenses.push({ id: doc.id, ...doc.data() } as RecordDocument);
      });
      callback(expenses);
    });
  }

  // 매장(가계부) 관련 메서드들
  async addStore(userId: string, storeData: Omit<StoreDocument, 'id' | 'createdAt' | 'updatedAt' | 'ownerId'>): Promise<string> {
    return this.addSubDocument(COLLECTIONS.STORES, userId, 'stores', {
      ...storeData,
      ownerId: userId
    });
  }

  async updateStore(userId: string, storeId: string, updateData: Partial<StoreDocument>): Promise<void> {
    try {
      const docRef = this.getSubDocRef(COLLECTIONS.STORES, userId, 'stores', storeId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error(`❌ 매장 정보 업데이트 실패: ${userId}/stores/${storeId}`, error);
      throw error;
    }
  }

  async deleteStore(userId: string, storeId: string): Promise<void> {
    try {
      const docRef = this.getSubDocRef(COLLECTIONS.STORES, userId, 'stores', storeId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error(`❌ 매장 삭제 실패: ${userId}/stores/${storeId}`, error);
      throw error;
    }
  }

  async getStore(userId: string, storeId: string): Promise<StoreDocument | null> {
    try {
      const storeRef = doc(db, COLLECTIONS.STORES, userId, 'stores', storeId);
      const storeDoc = await getDoc(storeRef);
      
      if (!storeDoc.exists()) {
        return null;
      }
      
      return { id: parseInt(storeDoc.id) || 0, ...storeDoc.data() } as StoreDocument;
    } catch (error) {
      console.error(`❌ 매장 조회 실패: ${userId}/stores/${storeId}`, error);
      throw error;
    }
  }

  async getStoreByAccountNumber(userId: string, accountNumber: number): Promise<StoreDocument | null> {
    try {
      const storesRef = collection(db, COLLECTIONS.STORES, userId, 'stores');
      const q = query(storesRef, where('accountNumber', '==', accountNumber));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      const doc = querySnapshot.docs[0];
      return { id: parseInt(doc.id) || 0, ...doc.data() } as StoreDocument;
    } catch (error) {
      console.error(`❌ 매장 조회 실패 (계정번호: ${accountNumber})`, error);
      throw error;
    }
  }

  // 매장 실시간 리스너
  onStoresSnapshot(
    userId: string,
    callback: (stores: StoreDocument[]) => void,
    options?: QueryOptions
  ): Unsubscribe {
    let q = query(this.getSubCollectionRef(COLLECTIONS.STORES, userId, 'stores'));

    // Where 조건 추가
    if (options?.whereConditions) {
      options.whereConditions.forEach(condition => {
        q = query(q, where(condition.field, condition.operator, condition.value));
      });
    }

    // 정렬 추가
    if (options?.orderByField) {
      q = query(q, orderBy(options.orderByField, options.orderDirection || 'asc'));
    }

    // 제한
    if (options?.limitCount) {
      q = query(q, limit(options.limitCount));
    }

    return onSnapshot(q, (querySnapshot) => {
      const stores: StoreDocument[] = [];
      querySnapshot.forEach((doc) => {
        stores.push({ id: parseInt(doc.id) || 0, ...doc.data() } as unknown as StoreDocument);
      });
      callback(stores);
    });
  }

  // Dashboard용 데이터 조회 메서드들
  
  // 특정 년도의 모든 거래 내역 데이터 조회 (가계부별 필터링)
  async getExpensesByYear(userId: string, year: number, accountNumber?: number): Promise<RecordDocument[]> {
    const startDate = new Date(year, 0, 1); // 해당 년도 1월 1일
    const endDate = new Date(year, 11, 31, 23, 59, 59); // 해당 년도 12월 31일 23:59:59
    
    
    try {
      // 먼저 모든 expenses 데이터를 가져와서 디버깅
      const allExpenses = await this.getSubCollection<RecordDocument>('users', userId, 'expenses');
      
      if (allExpenses.length > 0) {
        // 모든 expenses의 년도 분포 확인 (Flutter 앱의 실제 필드명 사용)
        const yearDistribution: { [key: number]: number } = {};
        allExpenses.forEach(expense => {
          let expenseDate: Date | null = null;
          
          // Flutter 앱의 createdTime 필드 사용
          if (expense.createdTime) {
            expenseDate = expense.createdTime.toDate();
          }
          
          if (expenseDate) {
            const year = expenseDate.getFullYear();
            yearDistribution[year] = (yearDistribution[year] || 0) + 1;
          }
        });
      }
      
      // 날짜 범위와 가계부로 필터링 (Flutter 앱의 실제 필드명 사용)
      const filteredExpenses = allExpenses.filter(expense => {
        let expenseDate: Date | null = null;
        
        // Flutter 앱의 createdTime 필드 사용
        if (expense.createdTime) {
          expenseDate = expense.createdTime.toDate();
        }
        
        if (!expenseDate) return false;
        
        // 날짜 필터링
        const matchesDate = expenseDate >= startDate && expenseDate <= endDate;
        
        // 가계부 필터링 (accountNumber가 제공된 경우에만)
        const matchesAccount = accountNumber === undefined || expense.accountNumber === accountNumber;
        
        return matchesDate && matchesAccount;
      });
      
      
      // 필터링된 데이터의 타입별 분포 확인
      if (filteredExpenses.length > 0) {
        const typeDistribution = filteredExpenses.reduce((acc, expense) => {
          acc[expense.incomeType] = (acc[expense.incomeType] || 0) + 1;
          return acc;
        }, {} as { [key: string]: number });
        
        // 필터링된 데이터의 년도 분포 확인
        const filteredYearDistribution: { [key: number]: number } = {};
        filteredExpenses.forEach(expense => {
          if (expense.createdTime) {
            const expenseDate = expense.createdTime.toDate();
            const expenseYear = expenseDate.getFullYear();
            filteredYearDistribution[expenseYear] = (filteredYearDistribution[expenseYear] || 0) + 1;
          }
        });
      }
      
      return filteredExpenses;
    } catch (error) {
      console.error('❌ getExpensesByYear 오류:', error);
      throw error;
    }
  }

  // 특정 년도의 수입/지출 데이터 조회 (type별, 가계부별 필터링)
  async getExpensesByYearAndType(userId: string, year: number, type: 'income' | 'expense', accountNumber?: number): Promise<RecordDocument[]> {
    const startDate = new Date(year, 0, 1);
    const endDate = new Date(year, 11, 31, 23, 59, 59);
    
    
    try {
      // 모든 expenses 데이터를 가져와서 클라이언트에서 필터링
      const allExpenses = await this.getSubCollection<RecordDocument>('users', userId, 'expenses');
      
      // 날짜, 타입, 가계부로 필터링 (Flutter 앱의 실제 필드명 사용)
      const filteredExpenses = allExpenses.filter(expense => {
        let expenseDate: Date | null = null;
        
        // Flutter 앱의 createdTime 필드 사용
        if (expense.createdTime) {
          expenseDate = expense.createdTime.toDate();
        }
        
        if (!expenseDate) return false;
        
        // 날짜 필터링
        const isInDateRange = expenseDate >= startDate && expenseDate <= endDate;
        
        // 타입 필터링 (Flutter 앱의 실제 incomeType 값에 맞게 수정)
        const isCorrectType = (type === 'income' && expense.incomeType === '매출') || 
                             (type === 'expense' && expense.incomeType === '지출');
        
        // 가계부 필터링 (accountNumber가 제공된 경우에만)
        const matchesAccount = accountNumber === undefined || expense.accountNumber === accountNumber;
        
        return isInDateRange && isCorrectType && matchesAccount;
      });
      
      return filteredExpenses;
    } catch (error) {
      console.error('❌ getExpensesByYearAndType 오류:', error);
      throw error;
    }
  }

  // 특정 년도의 월별 데이터 집계 (가계부별 필터링)
  async getMonthlyExpensesByYear(userId: string, year: number, accountNumber?: number): Promise<{
    month: number;
    income: number;
    expense: number;
    netIncome: number;
  }[]> {
    const expenses = await this.getExpensesByYear(userId, year, accountNumber);
    
    // 월별로 데이터 집계
    const monthlyData: { [key: number]: { income: number; expense: number } } = {};
    
    // 1월부터 12월까지 초기화
    for (let month = 1; month <= 12; month++) {
      monthlyData[month] = { income: 0, expense: 0 };
    }
    
    // 데이터 집계 (Flutter 앱의 실제 필드명 사용)
    expenses.forEach(expense => {
      let expenseDate: Date | null = null;
      
      // Flutter 앱의 createdTime 필드 사용
      if (expense.createdTime) {
        expenseDate = expense.createdTime.toDate();
      }
      
      if (!expenseDate) return;
      
      const month = expenseDate.getMonth() + 1; // 0-based이므로 +1
      const amount = Number(expense.money) || 0;
      
      // Flutter 앱의 실제 incomeType 값에 맞게 수정
      if (expense.incomeType === '매출' || expense.incomeType === 'income') {
        monthlyData[month].income += amount;
      } else if (expense.incomeType === '지출' || expense.incomeType === 'expense') {
        monthlyData[month].expense += amount;
      }
    });
    
    // 결과 배열로 변환
    return Object.entries(monthlyData).map(([month, data]) => ({
      month: parseInt(month),
      income: data.income,
      expense: data.expense,
      netIncome: data.income - data.expense
    }));
  }

  // 특정 년도의 카테고리별 지출 데이터 집계 (가계부별 필터링)
  async getCategoryExpensesByYear(userId: string, year: number, accountNumber?: number): Promise<{
    category: string;
    amount: number;
    percentage: number;
  }[]> {
    const expenses = await this.getExpensesByYearAndType(userId, year, 'expense', accountNumber); // '지출' 데이터
    
    // 카테고리별 집계 (Flutter 앱의 실제 필드명 사용)
    const categoryData: { [key: string]: number } = {};
    let totalAmount = 0;
    
    expenses.forEach(expense => {
      const category = expense.name; // Flutter 앱의 name 필드를 카테고리로 사용
      const amount = Number(expense.money) || 0;
      categoryData[category] = (categoryData[category] || 0) + amount;
      totalAmount += amount;
    });
    
    // 결과 배열로 변환 (비율 계산 포함)
    return Object.entries(categoryData).map(([category, amount]) => ({
      category,
      amount,
      percentage: totalAmount > 0 ? Math.round((amount / totalAmount) * 100) : 0
    })).sort((a, b) => b.amount - a.amount); // 금액 순으로 정렬
  }

  // 특정 년도의 수입원별 데이터 집계
  async getRevenueSourcesByYear(userId: string, year: number, accountNumber?: number): Promise<{
    category: string;
    amount: number;
    percentage: number;
  }[]> {
    const expenses = await this.getExpensesByYearAndType(userId, year, 'income', accountNumber); // '매출' 데이터
    
    // 카테고리별 집계 (Flutter 앱의 실제 필드명 사용)
    const categoryData: { [key: string]: number } = {};
    let totalAmount = 0;
    
    expenses.forEach(expense => {
      const category = expense.name; // Flutter 앱의 name 필드를 카테고리로 사용
      const amount = Number(expense.money) || 0;
      categoryData[category] = (categoryData[category] || 0) + amount;
      totalAmount += amount;
    });
    
    // 결과 배열로 변환 (비율 계산 포함)
    return Object.entries(categoryData).map(([category, amount]) => ({
      category,
      amount,
      percentage: totalAmount > 0 ? Math.round((amount / totalAmount) * 100) : 0
    })).sort((a, b) => b.amount - a.amount); // 금액 순으로 정렬
  }

  // 특정 년도의 KPI 데이터 계산 (가계부별 필터링)
  async getKPIDataByYear(userId: string, year: number, accountNumber?: number): Promise<{
    totalIncome: number;
    totalExpense: number;
    netIncome: number;
    recordCount: number;
    lastUpdated: Date;
  }> {
    
    const expenses = await this.getExpensesByYear(userId, year, accountNumber);
    
    let totalIncome = 0;
    let totalExpense = 0;
    let lastUpdated = new Date(0);
    
    expenses.forEach((expense, index) => {
      // Flutter 앱의 실제 필드명 사용
      const amount = Number(expense.money) || 0;
      const type = expense.incomeType;
      
      // Flutter 앱의 실제 incomeType 값에 맞게 수정
      if (type === '매출' || type === 'income') {
        totalIncome += amount;
      } else if (type === '지출' || type === 'expense') {
        totalExpense += amount;
      }
      
      // 가장 최근 업데이트 날짜 찾기
      const expenseDate = expense.createdTime?.toDate() || new Date(0);
      if (expenseDate > lastUpdated) {
        lastUpdated = expenseDate;
      }
    });
    
    const result = {
      totalIncome,
      totalExpense,
      netIncome: totalIncome - totalExpense,
      recordCount: expenses.length,
      lastUpdated
    };
    
    return result;
  }

  // ==================== SharedBooks 데이터 로딩 메서드 ====================

  /**
   * SharedBooks에서 사용자의 expenses 데이터 가져오기
   * @param userId 사용자 ID
   * @returns RecordDocument 배열
   */
  async getExpensesByUserFromShared(userId: string): Promise<RecordDocument[]> {
    try {
      
      const expensesRef = collection(db, COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.EXPENSES);
      const querySnapshot = await getDocs(expensesRef);
      
      const expenses: RecordDocument[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        expenses.push({
          id: doc.id,
          ...data,
          createdTime: data.createdTime || Timestamp.now(),
        } as RecordDocument);
      });
      
      return expenses;
    } catch (error) {
      console.error(`❌ SharedBooks expenses 로딩 실패: ${userId}`, error);
      throw error;
    }
  }

  /**
   * SharedBooks에서 사용자의 categories 데이터 가져오기
   * @param userId 사용자 ID
   * @returns Category 배열
   */
  async getCategoriesByUserFromShared(userId: string): Promise<Category[]> {
    try {
      
      const categoriesRef = collection(db, COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.CATEGORIES);
      const querySnapshot = await getDocs(categoriesRef);
      
      const categories: Category[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        categories.push({
          id: doc.id,
          name: data.name || '',
          type: data.type || 'expense',
          isActive: data.isActive !== false,
          sortOrder: data.sortOrder || 0,
          createdAt: data.createdAt || Date.now(),
          updatedAt: data.updatedAt || Date.now(),
        });
      });
      
      return categories;
    } catch (error) {
      console.error(`❌ SharedBooks categories 로딩 실패: ${userId}`, error);
      throw error;
    }
  }

  // ==================== 통합 데이터 로딩 메서드 ====================

  /**
   * 사용자 권한에 따른 통합 데이터 로딩
   * 프리미엄 사용자는 sharedBooks에서, 일반 사용자는 users에서 데이터를 로드
   * @param userId 사용자 ID
   * @returns 통합된 데이터와 데이터 소스 정보
   */
  async loadUserData(userId: string): Promise<{
    data: {
      expenses: RecordDocument[];
      merchants: any[];
      employees: any[];
      stores: StoreDocument[];
      categories: Category[];
      workSchedules: any[];
      repeatExpenses: RepeatRecordDocument[];
      todos: any[];
      history: any[];
    };
    dataSource: 'shared' | 'personal';
    isPremium: boolean;
    subscriptionType?: string;
  }> {
    try {
      
      // 1. 사용자 구독 상태 확인
      const { checkWebAccess, checkPremiumAccess, getCurrentSubscription } = await import('./subscriptionService');
      const [hasWebAccess, hasPremiumAccess, currentSubscription] = await Promise.all([
        checkWebAccess(userId),
        checkPremiumAccess(userId),
        getCurrentSubscription(userId)
      ]);
      
      
      // 2. 프리미엄 사용자 확인 (웹 접근 권한이 있으면 프리미엄으로 간주)
      // 개발/테스트용: sharedBooks 문서가 존재하면 강제로 프리미엄으로 처리
      const sharedBookExists = await this.getDocument<SharedBookDocument>(COLLECTIONS.SHARED_BOOKS, userId);
      const isPremium = hasWebAccess || !!sharedBookExists;
      
      if (isPremium) {
        try {
          const sharedData = await this.loadSharedBookData(userId);
          
          // stores 데이터 상세 로깅
          
          return {
            data: sharedData,
            dataSource: 'shared',
            isPremium: true,
            subscriptionType: 'PREMIUM'
          };
        } catch (error) {
          console.warn('⚠️ sharedBooks 데이터 로딩 실패, 개인 데이터로 fallback:', error);
          const personalData = await this.loadPersonalData(userId);
          return {
            data: personalData,
            dataSource: 'personal',
            isPremium: false,
            subscriptionType: 'FREE'
          };
        }
      } else {
        const personalData = await this.loadPersonalData(userId);
        return {
          data: personalData,
          dataSource: 'personal',
          isPremium: false,
          subscriptionType: 'FREE'
        };
      }
    } catch (error) {
      console.error('❌ loadUserData 실패:', error);
      // 최후의 수단으로 개인 데이터 로드
      try {
        const personalData = await this.loadPersonalData(userId);
        return {
          data: personalData,
          dataSource: 'personal',
          isPremium: false,
          subscriptionType: 'FREE'
        };
      } catch (fallbackError) {
        console.error('❌ 개인 데이터 로딩도 실패:', fallbackError);
        throw new Error('데이터 로딩에 실패했습니다.');
      }
    }
  }

  /**
   * 특정 년도의 통합 데이터 로딩 (대시보드용)
   * 프리미엄 사용자는 sharedBooks에서, 일반 사용자는 users에서 데이터를 로드
   * @param userId 사용자 ID
   * @param year 년도
   * @param accountNumber 가계부 번호 (선택사항)
   * @returns 통합된 대시보드 데이터와 데이터 소스 정보
   */
  // 프리미엄 판정 캐시 — loadDashboardData 가 연도별로 병렬 호출돼도 판정은 1번만 나간다
  private premiumCheckCache = new Map<string, { promise: Promise<boolean>; cachedAt: number }>();
  private static readonly PREMIUM_CHECK_TTL_MS = 60_000;

  private checkIsPremiumUser(userId: string): Promise<boolean> {
    const cached = this.premiumCheckCache.get(userId);
    if (cached && Date.now() - cached.cachedAt < FirestoreService.PREMIUM_CHECK_TTL_MS) {
      return cached.promise;
    }

    const promise = (async () => {
      const { checkWebAccess } = await import('./subscriptionService');
      // 개발/테스트용: sharedBooks 문서가 존재하면 강제로 프리미엄으로 처리
      const [hasWebAccess, sharedBookExists] = await Promise.all([
        checkWebAccess(userId),
        this.getDocument<SharedBookDocument>(COLLECTIONS.SHARED_BOOKS, userId),
      ]);
      return hasWebAccess || !!sharedBookExists;
    })();

    this.premiumCheckCache.set(userId, { promise, cachedAt: Date.now() });
    // 실패한 판정은 캐시하지 않는다 (다음 호출에서 재시도)
    promise.catch(() => this.premiumCheckCache.delete(userId));
    return promise;
  }

  async loadDashboardData(userId: string, year: number, accountNumber?: number): Promise<{
    kpiData: {
      totalIncome: number;
      totalExpense: number;
      netIncome: number;
      recordCount: number;
      lastUpdated: Date;
    };
    monthlyData: {
      month: number;
      income: number;
      expense: number;
      netIncome: number;
    }[];
    categoryExpenses: {
      category: string;
      amount: number;
      percentage: number;
    }[];
    revenueSources: {
      category: string;
      amount: number;
      percentage: number;
    }[];
    dataSource: 'shared' | 'personal';
    isPremium: boolean;
    subscriptionType?: string;
  }> {
    try {

      // 1. 사용자 권한 확인 (캐시 공유 — 연도별 병렬 호출 간 중복 제거)
      const isPremium = await this.checkIsPremiumUser(userId);


      if (isPremium) {
        try {
          // sharedBooks에서 데이터 로드
          const [kpiData, monthlyData, categoryExpenses, revenueSources] = await Promise.all([
            this.getKPIDataByYearFromShared(userId, year, accountNumber),
            this.getMonthlyExpensesByYearFromShared(userId, year, accountNumber),
            this.getCategoryExpensesByYearFromShared(userId, year, accountNumber),
            this.getRevenueSourcesByYearFromShared(userId, year, accountNumber)
          ]);
          
          return {
            kpiData,
            monthlyData,
            categoryExpenses,
            revenueSources,
            dataSource: 'shared',
            isPremium: true,
            subscriptionType: 'PREMIUM'
          };
        } catch (error) {
          console.warn('⚠️ sharedBooks 대시보드 데이터 로딩 실패, 개인 데이터로 fallback:', error);
          // fallback to personal data
          const [kpiData, monthlyData, categoryExpenses, revenueSources] = await Promise.all([
            this.getKPIDataByYear(userId, year, accountNumber),
            this.getMonthlyExpensesByYear(userId, year, accountNumber),
            this.getCategoryExpensesByYear(userId, year, accountNumber),
            this.getRevenueSourcesByYear(userId, year, accountNumber)
          ]);
          
          return {
            kpiData,
            monthlyData,
            categoryExpenses,
            revenueSources,
            dataSource: 'personal',
            isPremium: false,
            subscriptionType: 'FREE'
          };
        }
      } else {
        const [kpiData, monthlyData, categoryExpenses, revenueSources] = await Promise.all([
          this.getKPIDataByYear(userId, year, accountNumber),
          this.getMonthlyExpensesByYear(userId, year, accountNumber),
          this.getCategoryExpensesByYear(userId, year, accountNumber),
          this.getRevenueSourcesByYear(userId, year, accountNumber)
        ]);
        
        return {
          kpiData,
          monthlyData,
          categoryExpenses,
          revenueSources,
          dataSource: 'personal',
          isPremium: false,
          subscriptionType: 'FREE'
        };
      }
    } catch (error) {
      console.error('❌ loadDashboardData 실패:', error);
      throw error;
    }
  }

  // ==================== SharedBooks 관련 메서드 ====================

  /**
   * 공유 가계부 데이터 로딩 (프리미엄 사용자용)
   * @param userId 사용자 ID
   * @returns 공유 가계부 데이터
   */
  async loadSharedBookData(userId: string): Promise<{
    expenses: RecordDocument[];
    merchants: any[];
    employees: any[];
    stores: StoreDocument[];
    categories: Category[];
    workSchedules: any[];
    repeatExpenses: RepeatRecordDocument[];
    todos: any[];
    history: any[];
  }> {
    try {
      
      // 공유 가계부가 존재하는지 확인
      const sharedBookDoc = await this.getDocument<SharedBookDocument>(COLLECTIONS.SHARED_BOOKS, userId);
      if (!sharedBookDoc) {
        return await this.loadPersonalData(userId);
      }

      // hostId 확인 (프리미엄 사용자만 접근 가능)
      if (sharedBookDoc.hostId !== userId) {
        return await this.loadPersonalData(userId);
      }


      // 모든 하위 컬렉션을 병렬로 로드 (빈 컬렉션 허용)
      const [
        expenses,
        merchants,
        employees,
        stores,
        categories,
        workSchedules,
        repeatExpenses,
        todos,
        history
      ] = await Promise.allSettled([
        this.getSubCollection<RecordDocument>(COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.EXPENSES),
        this.getSubCollection(COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.MERCHANTS),
        this.getSubCollection(COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.EMPLOYEES),
        this.getSubCollection(COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.STORES) as unknown as Promise<StoreDocument[]>,
        this.getSubCollection(COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.CATEGORIES) as unknown as Promise<Category[]>,
        this.getSubCollection(COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.WORK_SCHEDULES),
        this.getSubCollection<RepeatRecordDocument>(COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.REPEAT_EXPENSES),
        this.getSubCollection(COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.TODO),
        this.getSubCollection(COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.HISTORY)
      ]).then(results => results.map(result => 
        result.status === 'fulfilled' ? result.value : []
      )) as [RecordDocument[], any[], any[], StoreDocument[], Category[], any[], RepeatRecordDocument[], any[], any[]];

      // stores 데이터 상세 로깅

      // 타입 안전성을 위해 categories를 명시적으로 캐스팅
      const typedCategories = categories as Category[];


      return {
        expenses,
        merchants,
        employees,
        stores,
        categories: typedCategories,
        workSchedules,
        repeatExpenses,
        todos,
        history
      };
    } catch (error) {
      console.error('❌ 공유 가계부 데이터 로딩 실패:', error);
      // 실패 시 개인 데이터로 fallback
      return await this.loadPersonalData(userId);
    }
  }

  /**
   * 개인 데이터 로딩 (일반 사용자용)
   * @param userId 사용자 ID
   * @returns 개인 데이터
   */
  async loadPersonalData(userId: string): Promise<{
    expenses: RecordDocument[];
    merchants: any[];
    employees: any[];
    stores: StoreDocument[];
    categories: Category[];
    workSchedules: any[];
    repeatExpenses: RepeatRecordDocument[];
    todos: any[];
    history: any[];
  }> {
    try {
      
      // 모든 하위 컬렉션을 병렬로 로드 (Flutter 앱과 일치)
      const [
        expenses,
        merchants,
        employees,
        stores,
        categories,
        workSchedules,
        repeatExpenses,
        todos,
        history
      ] = await Promise.all([
        this.getSubCollection<RecordDocument>(COLLECTIONS.USERS, userId, COLLECTIONS.EXPENSES),
        this.getSubCollection(COLLECTIONS.USERS, userId, COLLECTIONS.MERCHANTS),
        this.getSubCollection(COLLECTIONS.USERS, userId, COLLECTIONS.EMPLOYEES),
        this.getSubCollection(COLLECTIONS.USERS, userId, COLLECTIONS.STORES) as unknown as Promise<StoreDocument[]>,
        this.getSubCollection(COLLECTIONS.USERS, userId, COLLECTIONS.CATEGORIES) as unknown as Promise<Category[]>,
        this.getSubCollection(COLLECTIONS.USERS, userId, COLLECTIONS.WORK_SCHEDULES),
        this.getSubCollection<RepeatRecordDocument>(COLLECTIONS.USERS, userId, COLLECTIONS.REPEAT_EXPENSES),
        this.getSubCollection(COLLECTIONS.USERS, userId, COLLECTIONS.TODO),
        this.getSubCollection(COLLECTIONS.USERS, userId, COLLECTIONS.HISTORY)
      ]);

      // 타입 안전성을 위해 categories를 명시적으로 캐스팅
      const typedCategories = categories as Category[];


      return {
        expenses,
        merchants,
        employees,
        stores,
        categories: typedCategories,
        workSchedules,
        repeatExpenses,
        todos,
        history
      };
    } catch (error) {
      console.error('❌ 개인 데이터 로딩 실패:', error);
      throw error;
    }
  }

  /**
   * 공유 가계부에 지출/수입 추가
   * @param userId 사용자 ID
   * @param expenseData 지출/수입 데이터
   * @returns 생성된 문서 ID
   */
  async addSharedExpense(userId: string, expenseData: Omit<RecordDocument, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    return this.addSubDocument(COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.EXPENSES, expenseData);
  }

  /**
   * 공유 가계부에서 지출/수입 업데이트
   * @param userId 사용자 ID
   * @param expenseId 지출/수입 ID
   * @param updateData 업데이트할 데이터
   */
  async updateSharedExpense(userId: string, expenseId: string, updateData: Partial<RecordDocument>): Promise<void> {
    try {
      const docRef = this.getSubDocRef(COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.EXPENSES, expenseId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error(`❌ 공유 가계부 지출/수입 업데이트 실패: ${userId}/expenses/${expenseId}`, error);
      throw error;
    }
  }

  /**
   * 공유 가계부에서 지출/수입 삭제
   * @param userId 사용자 ID
   * @param expenseId 지출/수입 ID
   */
  async deleteSharedExpense(userId: string, expenseId: string): Promise<void> {
    try {
      const docRef = this.getSubDocRef(COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.EXPENSES, expenseId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error(`❌ 공유 가계부 지출/수입 삭제 실패: ${userId}/expenses/${expenseId}`, error);
      throw error;
    }
  }

  /**
   * 공유 가계부 실시간 리스너 설정
   * @param userId 사용자 ID
   * @param callback 콜백 함수
   * @param options 쿼리 옵션
   * @returns 구독 해제 함수
   */
  onSharedExpensesSnapshot(
    userId: string,
    callback: (expenses: RecordDocument[]) => void,
    options?: QueryOptions
  ): Unsubscribe {
    const collectionPath = `${COLLECTIONS.SHARED_BOOKS}/${userId}/${COLLECTIONS.EXPENSES}`;
    
    let q = query(this.getSubCollectionRef(COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.EXPENSES));

    // Where 조건 추가
    if (options?.whereConditions) {
      options.whereConditions.forEach(condition => {
        q = query(q, where(condition.field, condition.operator, condition.value));
      });
    }

    // 정렬 추가
    if (options?.orderByField) {
      q = query(q, orderBy(options.orderByField, options.orderDirection || 'desc'));
    }

    // 제한
    if (options?.limitCount) {
      q = query(q, limit(options.limitCount));
    }

    return onSnapshot(q, (querySnapshot) => {
      const expenses: RecordDocument[] = [];
      querySnapshot.forEach((doc) => {
        expenses.push({ id: doc.id, ...doc.data() } as RecordDocument);
      });
      
      
      callback(expenses);
    });
  }

  // ==================== SharedBooks 대시보드 메서드 ====================

  /**
   * sharedBooks에서 특정 년도의 KPI 데이터 조회
   */
  async getKPIDataByYearFromShared(userId: string, year: number, accountNumber?: number): Promise<{
    totalIncome: number;
    totalExpense: number;
    netIncome: number;
    recordCount: number;
    lastUpdated: Date;
  }> {
    
    try {
      // sharedBooks에서 직접 expenses 컬렉션 쿼리
      const expensesRef = collection(db, COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.EXPENSES);
      const querySnapshot = await getDocs(expensesRef);
      const allExpenses: RecordDocument[] = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as RecordDocument));
      
      const startDate = new Date(year, 0, 1);
      const endDate = new Date(year, 11, 31, 23, 59, 59);
      
      // 날짜 범위와 가계부로 필터링
      const filteredExpenses = allExpenses.filter(expense => {
        let expenseDate: Date | null = null;
        
        if (expense.createdTime) {
          expenseDate = expense.createdTime.toDate();
        }
        
        if (!expenseDate) return false;
        
        const matchesDate = expenseDate >= startDate && expenseDate <= endDate;
        const matchesAccount = accountNumber === undefined || expense.accountNumber === accountNumber;
        
        return matchesDate && matchesAccount;
      });
      
      
      let totalIncome = 0;
      let totalExpense = 0;
      let lastUpdated = new Date(0);
      
      filteredExpenses.forEach(expense => {
        const amount = Number(expense.money) || 0;
        const type = expense.incomeType;
        
        if (type === '매출' || type === 'income') {
          totalIncome += amount;
        } else if (type === '지출' || type === 'expense') {
          totalExpense += amount;
        }
        
        const expenseDate = expense.createdTime?.toDate() || new Date(0);
        if (expenseDate > lastUpdated) {
          lastUpdated = expenseDate;
        }
      });
      
      const result = {
        totalIncome,
        totalExpense,
        netIncome: totalIncome - totalExpense,
        recordCount: filteredExpenses.length,
        lastUpdated
      };
      
      return result;
    } catch (error) {
      console.error('❌ getKPIDataByYearFromShared 오류:', error);
      throw error;
    }
  }

  /**
   * sharedBooks에서 특정 년도의 월별 데이터 집계
   */
  async getMonthlyExpensesByYearFromShared(userId: string, year: number, accountNumber?: number): Promise<{
    month: number;
    income: number;
    expense: number;
    netIncome: number;
  }[]> {
    const expenses = await this.getExpensesByYearFromShared(userId, year, accountNumber);
    
    const monthlyData: { [key: number]: { income: number; expense: number } } = {};
    
    for (let month = 1; month <= 12; month++) {
      monthlyData[month] = { income: 0, expense: 0 };
    }
    
    expenses.forEach(expense => {
      let expenseDate: Date | null = null;
      
      if (expense.createdTime) {
        expenseDate = expense.createdTime.toDate();
      }
      
      if (!expenseDate) return;
      
      const month = expenseDate.getMonth() + 1;
      const amount = Number(expense.money) || 0;
      
      if (expense.incomeType === '매출' || expense.incomeType === 'income') {
        monthlyData[month].income += amount;
      } else if (expense.incomeType === '지출' || expense.incomeType === 'expense') {
        monthlyData[month].expense += amount;
      }
    });
    
    return Object.entries(monthlyData).map(([month, data]) => ({
      month: parseInt(month),
      income: data.income,
      expense: data.expense,
      netIncome: data.income - data.expense
    }));
  }

  /**
   * sharedBooks에서 특정 년도의 카테고리별 지출 데이터 집계
   */
  async getCategoryExpensesByYearFromShared(userId: string, year: number, accountNumber?: number): Promise<{
    category: string;
    amount: number;
    percentage: number;
  }[]> {
    const expenses = await this.getExpensesByYearAndTypeFromShared(userId, year, 'expense', accountNumber);
    
    const categoryData: { [key: string]: number } = {};
    let totalAmount = 0;
    
    expenses.forEach(expense => {
      const category = expense.name;
      const amount = Number(expense.money) || 0;
      categoryData[category] = (categoryData[category] || 0) + amount;
      totalAmount += amount;
    });
    
    return Object.entries(categoryData).map(([category, amount]) => ({
      category,
      amount,
      percentage: totalAmount > 0 ? Math.round((amount / totalAmount) * 100) : 0
    })).sort((a, b) => b.amount - a.amount);
  }

  /**
   * sharedBooks에서 특정 년도의 수입원별 데이터 집계
   */
  async getRevenueSourcesByYearFromShared(userId: string, year: number, accountNumber?: number): Promise<{
    category: string;
    amount: number;
    percentage: number;
  }[]> {
    const expenses = await this.getExpensesByYearAndTypeFromShared(userId, year, 'income', accountNumber);
    
    const categoryData: { [key: string]: number } = {};
    let totalAmount = 0;
    
    expenses.forEach(expense => {
      const category = expense.name;
      const amount = Number(expense.money) || 0;
      categoryData[category] = (categoryData[category] || 0) + amount;
      totalAmount += amount;
    });
    
    return Object.entries(categoryData).map(([category, amount]) => ({
      category,
      amount,
      percentage: totalAmount > 0 ? Math.round((amount / totalAmount) * 100) : 0
    })).sort((a, b) => b.amount - a.amount);
  }

  /**
   * sharedBooks에서 특정 년도의 모든 거래 내역 데이터 조회
   */
  async getExpensesByYearFromShared(userId: string, year: number, accountNumber?: number): Promise<RecordDocument[]> {
    const startDate = new Date(year, 0, 1);
    const endDate = new Date(year, 11, 31, 23, 59, 59);
    
    try {
      // sharedBooks에서 직접 expenses 컬렉션 쿼리
      const expensesRef = collection(db, COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.EXPENSES);
      const querySnapshot = await getDocs(expensesRef);
      const allExpenses: RecordDocument[] = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as RecordDocument));
      
      const filteredExpenses = allExpenses.filter(expense => {
        let expenseDate: Date | null = null;
        
        if (expense.createdTime) {
          expenseDate = expense.createdTime.toDate();
        }
        
        if (!expenseDate) return false;
        
        const matchesDate = expenseDate >= startDate && expenseDate <= endDate;
        const matchesAccount = accountNumber === undefined || expense.accountNumber === accountNumber;
        
        return matchesDate && matchesAccount;
      });
      
      return filteredExpenses;
    } catch (error) {
      console.error('❌ getExpensesByYearFromShared 오류:', error);
      throw error;
    }
  }

  /**
   * sharedBooks에서 특정 년도의 수입/지출 데이터 조회
   */
  async getExpensesByYearAndTypeFromShared(userId: string, year: number, type: 'income' | 'expense', accountNumber?: number): Promise<RecordDocument[]> {
    const startDate = new Date(year, 0, 1);
    const endDate = new Date(year, 11, 31, 23, 59, 59);
    
    try {
      // sharedBooks에서 직접 expenses 컬렉션 쿼리
      const expensesRef = collection(db, COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.EXPENSES);
      const querySnapshot = await getDocs(expensesRef);
      const allExpenses: RecordDocument[] = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as RecordDocument));
      
      const filteredExpenses = allExpenses.filter(expense => {
        let expenseDate: Date | null = null;
        
        if (expense.createdTime) {
          expenseDate = expense.createdTime.toDate();
        }
        
        if (!expenseDate) return false;
        
        const isInDateRange = expenseDate >= startDate && expenseDate <= endDate;
        const isCorrectType = (type === 'income' && expense.incomeType === '매출') || 
                             (type === 'expense' && expense.incomeType === '지출');
        const matchesAccount = accountNumber === undefined || expense.accountNumber === accountNumber;
        
        return isInDateRange && isCorrectType && matchesAccount;
      });
      
      return filteredExpenses;
    } catch (error) {
      console.error('❌ getExpensesByYearAndTypeFromShared 오류:', error);
      throw error;
    }
  }

  // ==================== SharedBooks 전용 메서드 ====================

  /**
   * sharedBooks에서 stores 데이터만 가져오기
   * @param userId 사용자 ID
   * @param accountNumber 가계부 번호 (선택사항: 제공되면 해당 accountNumber만 필터링)
   * @returns stores 데이터
   */
  async getSharedStores(userId: string, accountNumber?: number): Promise<StoreDocument[]> {
    try {
      
      // hostId 권한 확인
      const sharedBookDoc = await this.getDocument<SharedBookDocument>(COLLECTIONS.SHARED_BOOKS, userId);
      if (!sharedBookDoc) {
        return [];
      }

      if (sharedBookDoc.hostId !== userId) {
        return [];
      }

      // stores 컬렉션 참조 생성
      const storesRef = collection(db, COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.STORES);
      
      // accountNumber가 제공되면 쿼리에 필터 추가
      const q = accountNumber !== undefined
        ? query(storesRef, where('accountNumber', '==', accountNumber))
        : query(storesRef);
      
      const querySnapshot = await getDocs(q);
      const stores: StoreDocument[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const docId = parseInt(doc.id) || 0;
        
        
        // accountNumber 필드 검증 (필수 필드)
        if (data.accountNumber === undefined || data.accountNumber === null) {
          console.warn(`⚠️ getSharedStores: 문서 ${doc.id}에 accountNumber 필드가 없습니다. 문서 ID를 사용합니다.`);
        }
        
        // accountNumber 결정 (필드가 없으면 문서 ID 사용, 둘 다 없으면 1)
        const accountNumberValue = data.accountNumber !== undefined && data.accountNumber !== null
          ? data.accountNumber
          : (docId > 0 ? docId : 1);
        
        // id와 accountNumber 일치성 검증
        if (docId > 0 && docId !== accountNumberValue) {
          console.warn(`⚠️ getSharedStores: 문서 ID(${docId})와 accountNumber(${accountNumberValue})가 일치하지 않습니다. 문서에 따르면 일치해야 합니다.`);
        }
        
        // accountNumber 유효성 검증 (1-5 사이)
        if (accountNumberValue < 1 || accountNumberValue > 5) {
          console.warn(`⚠️ getSharedStores: accountNumber(${accountNumberValue})가 유효한 범위(1-5)를 벗어났습니다.`);
        }
        
        const storeData: StoreDocument = {
          id: docId,
          businessName: data.businessName || data.storeName || data.name || '자람',
          name: data.name || `${accountNumberValue}호점`,
          description: data.description,
          ownerId: data.ownerId,
          accountNumber: accountNumberValue, // 필수 필드
          isActive: data.isActive !== false,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          settings: data.settings,
          // 추가 필드들
          address: data.address,
          ownerPhone: data.ownerPhone,
          storePhone: data.storePhone,
          openingTime: data.openingTime,
          closingTime: data.closingTime,
          holidays: data.holidays,
          investment: data.investment,
          loan: data.loan,
          businessNumber: data.businessNumber,
          storeType: data.storeType,
        };
        
        stores.push(storeData);
      });

      return stores;
    } catch (error) {
      console.error('❌ getSharedStores 실패:', error);
      return [];
    }
  }

  /**
   * sharedBooks에서 특정 accountNumber의 매장 정보만 가져오기
   * @param userId 사용자 ID
   * @param accountNumber 가계부 번호 (1-5)
   * @returns 매장 정보 또는 null
   */
  async getSharedStoreByAccountNumber(userId: string, accountNumber: number): Promise<StoreDocument | null> {
    try {
      
      // accountNumber 유효성 검증
      if (accountNumber < 1 || accountNumber > 5) {
        console.error(`❌ getSharedStoreByAccountNumber: accountNumber(${accountNumber})가 유효한 범위(1-5)를 벗어났습니다.`);
        return null;
      }
      
      // hostId 권한 확인
      const sharedBookDoc = await this.getDocument<SharedBookDocument>(COLLECTIONS.SHARED_BOOKS, userId);
      if (!sharedBookDoc) {
        return null;
      }

      if (sharedBookDoc.hostId !== userId) {
        return null;
      }

      // stores 컬렉션에서 accountNumber로 쿼리
      const storesRef = collection(db, COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.STORES);
      const q = query(storesRef, where('accountNumber', '==', accountNumber));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      // 첫 번째 문서 가져오기 (accountNumber는 unique해야 함)
      const doc = querySnapshot.docs[0];
      const data = doc.data();
      const docId = parseInt(doc.id) || 0;
      
      // accountNumber 필드 검증
      if (data.accountNumber === undefined || data.accountNumber === null) {
        console.error(`❌ getSharedStoreByAccountNumber: 문서 ${doc.id}에 accountNumber 필드가 없습니다.`);
        return null;
      }
      
      // id와 accountNumber 일치성 검증
      if (docId > 0 && docId !== data.accountNumber) {
        console.warn(`⚠️ getSharedStoreByAccountNumber: 문서 ID(${docId})와 accountNumber(${data.accountNumber})가 일치하지 않습니다.`);
      }
      
      const storeData: StoreDocument = {
        id: docId,
        businessName: data.businessName || data.storeName || data.name || '자람',
        name: data.name || `${data.accountNumber}호점`,
        description: data.description,
        ownerId: data.ownerId,
        accountNumber: data.accountNumber,
        isActive: data.isActive !== false,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        settings: data.settings,
        address: data.address,
        ownerPhone: data.ownerPhone,
        storePhone: data.storePhone,
        openingTime: data.openingTime,
        closingTime: data.closingTime,
        holidays: data.holidays,
        investment: data.investment,
        loan: data.loan,
        businessNumber: data.businessNumber,
        storeType: data.storeType,
      };
      
      
      return storeData;
    } catch (error) {
      console.error(`❌ getSharedStoreByAccountNumber 실패 (accountNumber: ${accountNumber}):`, error);
      return null;
    }
  }

  // ==================== 디버깅 및 테스트 메서드 ====================

  /**
   * sharedBooks 컬렉션 구조 디버깅
   * @param userId 사용자 ID
   */
  async debugSharedBooksStructure(userId: string): Promise<void> {
    try {
      
      // 1. sharedBooks 문서 존재 확인
      const sharedBookDoc = await this.getDocument<SharedBookDocument>(COLLECTIONS.SHARED_BOOKS, userId);
      
      if (!sharedBookDoc) {
        return;
      }

      // 2. hostId 권한 확인
      if (sharedBookDoc.hostId !== userId) {
        return;
      }

      
      // 2. 각 하위 컬렉션 확인
      const subCollections = [
        COLLECTIONS.EXPENSES,
        COLLECTIONS.MERCHANTS,
        COLLECTIONS.EMPLOYEES,
        COLLECTIONS.STORES,
        COLLECTIONS.CATEGORIES,
        COLLECTIONS.WORK_SCHEDULES,
        COLLECTIONS.REPEAT_EXPENSES,
        COLLECTIONS.TODO,
        COLLECTIONS.HISTORY
      ];
      
      for (const collectionName of subCollections) {
        try {
          const data = await this.getSubCollection(COLLECTIONS.SHARED_BOOKS, userId, collectionName);
        } catch (error) {
          console.error(`❌ ${collectionName} 컬렉션 오류:`, error);
        }
      }
      
    } catch (error) {
      console.error('❌ sharedBooks 구조 디버깅 실패:', error);
    }
  }

  /**
   * 사용자 구독 상태 디버깅
   * @param userId 사용자 ID
   */
  async debugUserSubscription(userId: string): Promise<void> {
    try {
      
      const { checkWebAccess, checkPremiumAccess, getCurrentSubscription } = await import('./subscriptionService');
      
      const [webAccess, premiumAccess, subscription] = await Promise.all([
        checkWebAccess(userId),
        checkPremiumAccess(userId),
        getCurrentSubscription(userId)
      ]);
      
      
    } catch (error) {
      console.error('❌ 사용자 구독 상태 디버깅 실패:', error);
    }
  }

  // ==================== 직원 관리 관련 메서드 ====================

  /**
   * SharedBooks에서 사용자의 직원 목록을 가져옵니다
   * @param userId 사용자 ID
   * @param accountNumber 가계부 번호 (선택사항)
   * @returns 직원 목록
   */
  async getEmployeesByUserFromShared(userId: string, accountNumber?: number): Promise<any[]> {
    try {
      
      const employeesRef = collection(db, COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.EMPLOYEES);
      
      // 쿼리 조건 구성
      let q = query(employeesRef);
      
      // 가계부 번호로 필터링 (Flutter는 account_number를 사용)
      if (accountNumber !== undefined) {
        q = query(q, where('account_number', '==', accountNumber));
      }
      
      // 정렬 추가
      q = query(q, orderBy('createdAt', 'desc'));
      
      const querySnapshot = await getDocs(q);
      const employees: any[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        employees.push(mapEmployeeFromFirestore(doc.id, data as Record<string, unknown>));
      });
      
      
      return employees;
    } catch (error) {
      console.error('❌ getEmployeesByUserFromShared 실패:', error);
      throw error;
    }
  }

  /**
   * SharedBooks에서 사용자의 근무 일정 목록을 가져옵니다
   * @param userId 사용자 ID
   * @param selectedDate 선택된 날짜 (선택사항)
   * @param accountNumber 가계부 번호 (선택사항)
   * @returns 근무 일정 목록
   */
  async getWorkSchedulesByUserFromShared(userId: string, selectedDate?: Date, accountNumber?: number): Promise<any[]> {
    try {
      
      const schedulesRef = collection(db, COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.WORK_SCHEDULES);
      
      // 쿼리 조건 구성
      let q = query(schedulesRef);
      
      // 가계부 번호로 필터링
      if (accountNumber !== undefined) {
        q = query(q, where('account_number', '==', accountNumber));
      }
      
      // 날짜 필터링
      if (selectedDate) {
        const startOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
        const endOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);

        const startDateStr = formatLocalDateString(startOfMonth);
        const endDateStr = formatLocalDateString(endOfMonth);

        q = query(q, where('date', '>=', startDateStr));
        q = query(q, where('date', '<=', endDateStr));
      }
      
      // 정렬 추가
      q = query(q, orderBy('date', 'desc'));
      
      const querySnapshot = await getDocs(q);
      const schedules: WorkSchedule[] = [];
      
      querySnapshot.forEach((docSnap) => {
        schedules.push(mapWorkScheduleFromFirestore(docSnap.id, docSnap.data() as Record<string, unknown>));
      });
      
      
      return schedules;
    } catch (error) {
      console.error('❌ getWorkSchedulesByUserFromShared 실패:', error);
      throw error;
    }
  }

  /**
   * SharedBooks에 직원을 추가합니다
   * @param userId 사용자 ID
   * @param employeeData 직원 데이터
   * @returns 생성된 직원 ID
   */
  async addEmployeeToShared(userId: string, employeeData: any): Promise<string> {
    try {
      
      const employeesRef = collection(db, COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.EMPLOYEES);
      
      const normalizeHireDate = (value: any): Timestamp | null => {
        if (!value) return null;
        if (value instanceof Timestamp) return value;
        if (value instanceof Date) return Timestamp.fromDate(value);
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (/^\d{4}\.\s?\d{2}\.\s?\d{2}\.?$/.test(trimmed)) {
            const digits = trimmed.replace(/[^0-9]/g, '');
            if (digits.length === 8) {
              const year = Number(digits.slice(0, 4));
              const month = Number(digits.slice(4, 6)) - 1;
              const day = Number(digits.slice(6, 8));
              return Timestamp.fromDate(new Date(year, month, day));
            }
          }
          const parsed = new Date(trimmed.length === 10 ? `${trimmed}T00:00:00` : trimmed);
          return isNaN(parsed.getTime()) ? null : Timestamp.fromDate(parsed);
        }
        if (typeof value === 'number') {
          const parsed = new Date(value);
          return isNaN(parsed.getTime()) ? null : Timestamp.fromDate(parsed);
        }
        if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
          return Timestamp.fromDate(value.toDate());
        }
        return null;
      };

      const normalizedHireDate = normalizeHireDate(employeeData.hire_date ?? employeeData.hireDate);
      const normalizedEmployee = normalizeEmployeeForFirestore({
        ...employeeData,
        hire_date: normalizedHireDate,
        hireDate: normalizedHireDate,
      });

      const newEmployeeData = {
        ...normalizedEmployee,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      
      const documentId = employeeData.id || employeeData.documentId || employeeData.docId;
      const docRef = documentId && typeof documentId === 'string'
        ? doc(employeesRef, documentId)
        : doc(employeesRef);

      await setDoc(docRef, newEmployeeData);
      
      return docRef.id;
    } catch (error) {
      console.error('❌ addEmployeeToShared 실패:', error);
      throw error;
    }
  }

  /**
   * SharedBooks의 직원 정보를 업데이트합니다
   * @param userId 사용자 ID
   * @param employeeId 직원 ID
   * @param updateData 업데이트할 데이터
   */
  async updateEmployeeInShared(userId: string, employeeId: string, updateData: any): Promise<void> {
    try {
      
      const employeeRef = doc(db, COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.EMPLOYEES, employeeId);
      
      const normalizeHireDate = (value: any): Timestamp | null => {
        if (!value) return null;
        if (value instanceof Timestamp) return value;
        if (value instanceof Date) return Timestamp.fromDate(value);
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (/^\d{4}\.\s?\d{2}\.\s?\d{2}\.?$/.test(trimmed)) {
            const digits = trimmed.replace(/[^0-9]/g, '');
            if (digits.length === 8) {
              const year = Number(digits.slice(0, 4));
              const month = Number(digits.slice(4, 6)) - 1;
              const day = Number(digits.slice(6, 8));
              return Timestamp.fromDate(new Date(year, month, day));
            }
          }
          const parsed = new Date(trimmed.length === 10 ? `${trimmed}T00:00:00` : trimmed);
          return isNaN(parsed.getTime()) ? null : Timestamp.fromDate(parsed);
        }
        if (typeof value === 'number') {
          const parsed = new Date(value);
          return isNaN(parsed.getTime()) ? null : Timestamp.fromDate(parsed);
        }
        if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
          return Timestamp.fromDate(value.toDate());
        }
        return null;
      };

      const normalizedHireDate = normalizeHireDate(updateData.hire_date ?? updateData.hireDate);
      const normalizedUpdate = normalizeEmployeeForFirestore({
        ...updateData,
        hire_date: normalizedHireDate ?? updateData.hire_date ?? updateData.hireDate ?? null,
        hireDate: normalizedHireDate ?? updateData.hireDate ?? updateData.hire_date ?? null,
      });

      await updateDoc(employeeRef, {
        ...normalizedUpdate,
        updatedAt: serverTimestamp(),
      });
      
    } catch (error) {
      console.error('❌ updateEmployeeInShared 실패:', error);
      throw error;
    }
  }

  /**
   * SharedBooks에서 직원을 삭제합니다
   * @param userId 사용자 ID
   * @param employeeId 직원 ID
   */
  async deleteEmployeeFromShared(userId: string, employeeId: string): Promise<void> {
    try {
      
      const employeeRef = doc(db, COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.EMPLOYEES, employeeId);
      await deleteDoc(employeeRef);
      
    } catch (error) {
      console.error('❌ deleteEmployeeFromShared 실패:', error);
      throw error;
    }
  }

  /**
   * SharedBooks에 근무 일정을 추가합니다
   * @param userId 사용자 ID
   * @param scheduleId 스케줄 ID (UUID)
   * @param scheduleData 근무 일정 데이터
   * @returns 생성된 일정 ID
   */
  async addWorkScheduleToShared(userId: string, scheduleId: string, scheduleData: any): Promise<string> {
    try {
      
      const scheduleRef = doc(db, COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.WORK_SCHEDULES, scheduleId);
      
      // 템플릿 기반 스케줄 데이터 변환 (workTime1/workTime2 → start_time_1/end_time_1 등)
      const firestoreData: any = { ...scheduleData };
      
      if (scheduleData.workTime1) {
        // 문자열 형식으로 저장 (예: "2025-12-24T07:10:00.000")
        const start1 = scheduleData.workTime1.start instanceof Date 
          ? scheduleData.workTime1.start 
          : new Date(scheduleData.workTime1.start);
        const end1 = scheduleData.workTime1.end instanceof Date 
          ? scheduleData.workTime1.end 
          : new Date(scheduleData.workTime1.end);
        firestoreData.start_time_1 = start1.toISOString().slice(0, -1); // 'Z' 제거하고 .000 형식
        firestoreData.end_time_1 = end1.toISOString().slice(0, -1);
      }
      
      if (scheduleData.workTime2) {
        // 문자열 형식으로 저장 (예: "2025-12-24T18:10:00.000")
        const start2 = scheduleData.workTime2.start instanceof Date 
          ? scheduleData.workTime2.start 
          : new Date(scheduleData.workTime2.start);
        const end2 = scheduleData.workTime2.end instanceof Date 
          ? scheduleData.workTime2.end 
          : new Date(scheduleData.workTime2.end);
        firestoreData.start_time_2 = start2.toISOString().slice(0, -1); // 'Z' 제거하고 .000 형식
        firestoreData.end_time_2 = end2.toISOString().slice(0, -1);
      }
      
      if (scheduleData.templateId) {
        firestoreData.template_id = scheduleData.templateId;
      }
      
      if (scheduleData.hourlyRate !== undefined) {
        firestoreData.hourly_rate = scheduleData.hourlyRate;
      }
      
      // Flutter 앱 호환성: accountNumber도 저장
      if (scheduleData.accountNumber !== undefined) {
        firestoreData.accountNumber = scheduleData.accountNumber;
      }
      
      // Flutter 앱 호환성: severance_pay가 null이면 0으로 저장
      if (firestoreData.severance_pay === null || firestoreData.severance_pay === undefined) {
        firestoreData.severance_pay = 0;
      }

      // 입사일 미입력 직원: hire_date가 undefined면 Firestore가 거부하므로 null로 정규화
      if (firestoreData.hire_date === undefined) {
        firestoreData.hire_date = null;
      }

      // work_times 배열은 저장하지 않음 (Flutter 앱에서 저장하지 않음)
      delete firestoreData.work_times;
      
      // start_time, end_time을 문자열 형식으로 저장 (예: "2025-12-24T00:00:00.000")
      // Flutter 앱 호환성: workTime1이 있으면 start_time, end_time에도 저장 (하위 호환성)
      if (scheduleData.start_time) {
        const startTime = typeof scheduleData.start_time === 'string' 
          ? new Date(scheduleData.start_time)
          : (scheduleData.start_time instanceof Date ? scheduleData.start_time : new Date(scheduleData.start_time));
        firestoreData.start_time = startTime.toISOString().slice(0, -1); // 'Z' 제거
        
        // workTime1이 없고 start_time_1도 없으면 start_time_1, end_time_1도 설정 (Flutter toFirebaseMap 로직)
        if (!firestoreData.start_time_1 && scheduleData.end_time) {
          const endTime = typeof scheduleData.end_time === 'string'
            ? new Date(scheduleData.end_time)
            : (scheduleData.end_time instanceof Date ? scheduleData.end_time : new Date(scheduleData.end_time));
          firestoreData.start_time_1 = startTime.toISOString().slice(0, -1);
          firestoreData.end_time_1 = endTime.toISOString().slice(0, -1);
        }
      }
      if (scheduleData.end_time) {
        const endTime = typeof scheduleData.end_time === 'string'
          ? new Date(scheduleData.end_time)
          : (scheduleData.end_time instanceof Date ? scheduleData.end_time : new Date(scheduleData.end_time));
        firestoreData.end_time = endTime.toISOString().slice(0, -1); // 'Z' 제거
      }
      
      // date 필드를 ISO 형식으로 저장 (예: "2025-12-24T00:00:00.000Z")
      if (scheduleData.date) {
        if (typeof scheduleData.date === 'string') {
          // 이미 ISO 형식이면 그대로 사용, YYYY-MM-DD 형식이면 변환
          if (scheduleData.date.includes('T')) {
            firestoreData.date = scheduleData.date.endsWith('Z') ? scheduleData.date : `${scheduleData.date}Z`;
          } else {
            firestoreData.date = `${scheduleData.date}T00:00:00.000Z`;
          }
        } else if (scheduleData.date instanceof Date) {
          firestoreData.date = scheduleData.date.toISOString();
        }
      }
      
      // workTime1, workTime2는 Firestore에 저장하지 않음 (start_time_1 등으로 변환됨)
      delete firestoreData.workTime1;
      delete firestoreData.workTime2;
      delete firestoreData.templateId;
      
      // ownerId는 저장하지 않음 (Flutter 앱에서 저장하지 않음)
      delete firestoreData.ownerId;

      // V53: 직원 연결 시 스냅샷 이름 제거
      if (firestoreData.employee_id) {
        delete firestoreData.snapshot_employee_name;
        delete firestoreData.snapshotEmployeeName;
      }
      
      // createdAt, updatedAt을 숫자 타임스탬프로 저장
      const now = Date.now();
      const newScheduleData = {
        ...firestoreData,
        createdAt: now,
        updatedAt: now
      };

      // Firestore는 undefined 값을 거부하므로 남은 undefined 필드 제거 (방어)
      Object.keys(newScheduleData).forEach((key) => {
        if ((newScheduleData as any)[key] === undefined) {
          delete (newScheduleData as any)[key];
        }
      });

      await setDoc(scheduleRef, newScheduleData);
      
      return scheduleId;
    } catch (error) {
      console.error('❌ addWorkScheduleToShared 실패:', error);
      throw error;
    }
  }

  /**
   * SharedBooks의 근무 일정을 업데이트합니다
   * @param userId 사용자 ID
   * @param scheduleId 일정 ID
   * @param updateData 업데이트할 데이터
   */
  async updateWorkScheduleInShared(userId: string, scheduleId: string, updateData: any): Promise<void> {
    try {
      
      const scheduleRef = doc(db, COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.WORK_SCHEDULES, scheduleId);
      
      // 템플릿 기반 스케줄 데이터 변환 (workTime1/workTime2 → start_time_1/end_time_1 등)
      const firestoreData: any = { ...updateData };
      
      if (updateData.workTime1) {
        // 문자열 형식으로 저장 (예: "2025-12-24T07:10:00.000")
        const start1 = updateData.workTime1.start instanceof Date 
          ? updateData.workTime1.start 
          : new Date(updateData.workTime1.start);
        const end1 = updateData.workTime1.end instanceof Date 
          ? updateData.workTime1.end 
          : new Date(updateData.workTime1.end);
        firestoreData.start_time_1 = start1.toISOString().slice(0, -1); // 'Z' 제거
        firestoreData.end_time_1 = end1.toISOString().slice(0, -1);
      }
      
      if (updateData.workTime2) {
        // 문자열 형식으로 저장 (예: "2025-12-24T18:10:00.000")
        const start2 = updateData.workTime2.start instanceof Date 
          ? updateData.workTime2.start 
          : new Date(updateData.workTime2.start);
        const end2 = updateData.workTime2.end instanceof Date 
          ? updateData.workTime2.end 
          : new Date(updateData.workTime2.end);
        firestoreData.start_time_2 = start2.toISOString().slice(0, -1); // 'Z' 제거
        firestoreData.end_time_2 = end2.toISOString().slice(0, -1);
      }
      
      if (updateData.templateId !== undefined) {
        firestoreData.template_id = updateData.templateId;
      }
      
      if (updateData.hourlyRate !== undefined) {
        firestoreData.hourly_rate = updateData.hourlyRate;
      }
      
      // Flutter 앱 호환성: accountNumber도 저장
      if (updateData.accountNumber !== undefined) {
        firestoreData.accountNumber = updateData.accountNumber;
      }
      
      // Flutter 앱 호환성: severance_pay가 null이면 0으로 저장
      if (updateData.severance_pay !== undefined) {
        firestoreData.severance_pay = updateData.severance_pay ?? 0;
      }
      
      // work_times 배열은 저장하지 않음 (Flutter 앱에서 저장하지 않음)
      delete firestoreData.work_times;
      
      // start_time, end_time을 문자열 형식으로 저장 (예: "2025-12-24T00:00:00.000")
      // Flutter 앱 호환성: workTime1이 있으면 start_time, end_time에도 저장 (하위 호환성)
      if (updateData.start_time !== undefined) {
        const startTime = typeof updateData.start_time === 'string' 
          ? new Date(updateData.start_time)
          : (updateData.start_time instanceof Date ? updateData.start_time : new Date(updateData.start_time));
        firestoreData.start_time = startTime.toISOString().slice(0, -1); // 'Z' 제거
        
        // start_time_1이 없고 end_time도 있으면 start_time_1, end_time_1도 설정 (Flutter toFirebaseMap 로직)
        if (!firestoreData.start_time_1 && updateData.end_time !== undefined) {
          const endTime = typeof updateData.end_time === 'string'
            ? new Date(updateData.end_time)
            : (updateData.end_time instanceof Date ? updateData.end_time : new Date(updateData.end_time));
          firestoreData.start_time_1 = startTime.toISOString().slice(0, -1);
          firestoreData.end_time_1 = endTime.toISOString().slice(0, -1);
        }
      }
      if (updateData.end_time !== undefined) {
        const endTime = typeof updateData.end_time === 'string'
          ? new Date(updateData.end_time)
          : (updateData.end_time instanceof Date ? updateData.end_time : new Date(updateData.end_time));
        firestoreData.end_time = endTime.toISOString().slice(0, -1); // 'Z' 제거
      }
      
      // start_time_1, end_time_1이 직접 전달된 경우 처리
      if (updateData.start_time_1 !== undefined) {
        const startTime1 = typeof updateData.start_time_1 === 'string' 
          ? new Date(updateData.start_time_1)
          : (updateData.start_time_1 instanceof Date ? updateData.start_time_1 : new Date(updateData.start_time_1));
        firestoreData.start_time_1 = startTime1.toISOString().slice(0, -1); // 'Z' 제거
      }
      if (updateData.end_time_1 !== undefined) {
        const endTime1 = typeof updateData.end_time_1 === 'string'
          ? new Date(updateData.end_time_1)
          : (updateData.end_time_1 instanceof Date ? updateData.end_time_1 : new Date(updateData.end_time_1));
        firestoreData.end_time_1 = endTime1.toISOString().slice(0, -1); // 'Z' 제거
      }
      
      // date 필드를 ISO 형식으로 저장 (예: "2025-12-24T00:00:00.000Z")
      if (updateData.date !== undefined) {
        if (typeof updateData.date === 'string') {
          if (updateData.date.includes('T')) {
            firestoreData.date = updateData.date.endsWith('Z') ? updateData.date : `${updateData.date}Z`;
          } else {
            firestoreData.date = `${updateData.date}T00:00:00.000Z`;
          }
        } else if (updateData.date instanceof Date) {
          firestoreData.date = updateData.date.toISOString();
        }
      }
      
      // workTime1, workTime2는 Firestore에 저장하지 않음 (start_time_1 등으로 변환됨)
      delete firestoreData.workTime1;
      delete firestoreData.workTime2;
      delete firestoreData.templateId;
      
      // ownerId는 저장하지 않음 (Flutter 앱에서 저장하지 않음)
      delete firestoreData.ownerId;

      // V53: 직원 연결 시 스냅샷 이름 제거
      if (firestoreData.employee_id) {
        firestoreData.snapshot_employee_name = deleteField();
        delete firestoreData.snapshotEmployeeName;
      }
      
      const updatePayload: any = {
        ...firestoreData,
        updatedAt: Date.now() // 숫자 타임스탬프로 저장
      };

      // Firestore는 undefined 값을 거부하므로 남은 undefined 필드 제거 (방어)
      Object.keys(updatePayload).forEach((key) => {
        if (updatePayload[key] === undefined) {
          delete updatePayload[key];
        }
      });

      await updateDoc(scheduleRef, updatePayload);

    } catch (error) {
      console.error('❌ updateWorkScheduleInShared 실패:', error);
      throw error;
    }
  }

  /**
   * 직원의 근무 일정에서 employee_id를 분리하고 이름 스냅샷을 저장합니다 (V53 — 근무 기록 유지)
   */
  async detachEmployeeFromSchedules(
    userId: string,
    employeeId: string,
    accountNumber: number,
    employeeName: string,
  ): Promise<number> {
    try {

      const schedulesRef = collection(db, COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.WORK_SCHEDULES);
      const q = query(schedulesRef, where('employee_id', '==', employeeId));
      const querySnapshot = await getDocs(q);

      const matchingDocs = querySnapshot.docs.filter((docSnap) => {
        const data = docSnap.data();
        const docAccount = data.account_number ?? data.accountNumber;
        return docAccount === accountNumber;
      });

      if (matchingDocs.length === 0) {
        return 0;
      }

      const BATCH_LIMIT = 500;
      for (let i = 0; i < matchingDocs.length; i += BATCH_LIMIT) {
        const chunk = matchingDocs.slice(i, i + BATCH_LIMIT);
        const batch = writeBatch(db);
        chunk.forEach((docSnap) => {
          batch.update(docSnap.ref, {
            employee_id: deleteField(),
            employee_name: deleteField(),
            snapshot_employee_name: employeeName,
            updatedAt: Date.now(),
          });
        });
        await batch.commit();
      }

      return matchingDocs.length;
    } catch (error) {
      console.error('[퇴직자삭제] detachEmployeeFromSchedules 실패:', error);
      throw error;
    }
  }

  /**
   * 직원의 모든 근무 일정을 삭제합니다 (근무 기록까지 모두 삭제)
   */
  async deleteWorkSchedulesByEmployee(
    userId: string,
    employeeId: string,
    accountNumber: number,
  ): Promise<number> {
    try {

      const schedulesRef = collection(db, COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.WORK_SCHEDULES);
      const q = query(schedulesRef, where('employee_id', '==', employeeId));
      const querySnapshot = await getDocs(q);

      const matchingDocs = querySnapshot.docs.filter((docSnap) => {
        const data = docSnap.data();
        const docAccount = data.account_number ?? data.accountNumber;
        return docAccount === accountNumber;
      });

      if (matchingDocs.length === 0) {
        return 0;
      }

      const BATCH_LIMIT = 500;
      for (let i = 0; i < matchingDocs.length; i += BATCH_LIMIT) {
        const chunk = matchingDocs.slice(i, i + BATCH_LIMIT);
        const batch = writeBatch(db);
        chunk.forEach((docSnap) => batch.delete(docSnap.ref));
        await batch.commit();
      }

      return matchingDocs.length;
    } catch (error) {
      console.error('[퇴직자삭제] deleteWorkSchedulesByEmployee 실패:', error);
      throw error;
    }
  }

  /**
   * SharedBooks에서 근무 일정을 삭제합니다
   * @param userId 사용자 ID
   * @param scheduleId 일정 ID
   */
  async deleteWorkScheduleFromShared(userId: string, scheduleId: string): Promise<void> {
    try {
      
      const scheduleRef = doc(db, COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.WORK_SCHEDULES, scheduleId);
      await deleteDoc(scheduleRef);
      
    } catch (error) {
      console.error('❌ deleteWorkScheduleFromShared 실패:', error);
      throw error;
    }
  }

  /**
   * 직원 실시간 스냅샷을 설정합니다
   * @param userId 사용자 ID
   * @param callback 콜백 함수
   * @param options 쿼리 옵션
   * @returns 구독 해제 함수
   */
  onEmployeesSnapshot(
    userId: string,
    callback: (employees: any[]) => void,
    options?: QueryOptions
  ): Unsubscribe {
    try {
      
      const employeesRef = collection(db, COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.EMPLOYEES);
      let q = query(employeesRef);
      
      // Where 조건 추가
      if (options?.whereConditions) {
        options.whereConditions.forEach(condition => {
          q = query(q, where(condition.field, condition.operator, condition.value));
        });
      }
      
      // 정렬 추가
      if (options?.orderByField) {
        q = query(q, orderBy(options.orderByField, options.orderDirection || 'desc'));
      }
      
      // 제한
      if (options?.limitCount) {
        q = query(q, limit(options.limitCount));
      }
      
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const employees: any[] = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          employees.push(mapEmployeeFromFirestore(doc.id, data as Record<string, unknown>));
        });
        
        
        callback(employees);
      }, (error) => {
        console.error('❌ onEmployeesSnapshot 에러:', error);
      });
      
      return unsubscribe;
    } catch (error) {
      console.error('❌ onEmployeesSnapshot 설정 실패:', error);
      throw error;
    }
  }

  /**
   * 근무 일정 실시간 스냅샷을 설정합니다
   * @param userId 사용자 ID
   * @param callback 콜백 함수
   * @param options 쿼리 옵션
   * @returns 구독 해제 함수
   */
  onWorkSchedulesSnapshot(
    userId: string,
    callback: (schedules: any[]) => void,
    options?: QueryOptions
  ): Unsubscribe {
    try {
      
      const schedulesRef = collection(db, COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.WORK_SCHEDULES);
      let q = query(schedulesRef);
      
      // Where 조건 추가
      if (options?.whereConditions) {
        options.whereConditions.forEach(condition => {
          q = query(q, where(condition.field, condition.operator, condition.value));
        });
      }
      
      // 정렬 추가
      if (options?.orderByField) {
        q = query(q, orderBy(options.orderByField, options.orderDirection || 'desc'));
      }
      
      // 제한
      if (options?.limitCount) {
        q = query(q, limit(options.limitCount));
      }
      
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const schedules: WorkSchedule[] = [];

        // 변경사항 추적 (추가/수정/삭제)
        const changes = querySnapshot.docChanges();
        const added = changes.filter(c => c.type === 'added').length;
        const modified = changes.filter(c => c.type === 'modified').length;
        const removed = changes.filter(c => c.type === 'removed').length;

        querySnapshot.forEach((docSnap) => {
          schedules.push(mapWorkScheduleFromFirestore(docSnap.id, docSnap.data() as Record<string, unknown>));
        });
        
        // 초기 로드인지 실시간 변경인지 구분
        const isInitialLoad = changes.length === schedules.length && added === schedules.length;
        
        
        callback(schedules);
      }, (error) => {
        console.error('❌ onWorkSchedulesSnapshot 에러:', error);
      });
      
      return unsubscribe;
    } catch (error) {
      console.error('❌ onWorkSchedulesSnapshot 설정 실패:', error);
      throw error;
    }
  }

  // ==================== 카테고리 관련 메서드 ====================

  /**
   * 사용자의 카테고리 목록을 가져옵니다
   * @param userId 사용자 ID
   * @param options 필터링 옵션
   * @returns 카테고리 목록
   */
  async getUserCategories(userId: string, options: CategoryFilterOptions = {}): Promise<Category[]> {
    try {
      
      // 사용자별 카테고리 컬렉션 경로: users/{userId}/categories
      const categoriesRef = collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.CATEGORIES);
      
      // 쿼리 조건 구성
      let q = query(categoriesRef, orderBy('sortOrder', 'asc'));
      
      // 타입 필터링
      if (options.type) {
        q = query(q, where('type', '==', options.type));
      }
      
      // 활성화 상태 필터링
      if (options.isActive !== undefined) {
        q = query(q, where('isActive', '==', options.isActive));
      }
      
      const querySnapshot = await getDocs(q);
      const categories: Category[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        categories.push({
          id: doc.id,
          name: data.name || '',
          type: data.type || 'expense',
          isActive: data.isActive !== false, // 기본값 true
          sortOrder: data.sortOrder || 0,
          createdAt: data.createdAt || Date.now(),
          updatedAt: data.updatedAt || Date.now(),
        });
      });
      
      
      return categories;
    } catch (error) {
      console.error('❌ getUserCategories 실패:', error);
      throw error;
    }
  }

  /**
   * 특정 카테고리를 가져옵니다
   * @param userId 사용자 ID
   * @param categoryId 카테고리 ID
   * @returns 카테고리 정보
   */
  async getCategory(userId: string, categoryId: string): Promise<Category | null> {
    try {
      
      const categoryRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.CATEGORIES, categoryId);
      const categoryDoc = await getDoc(categoryRef);
      
      if (!categoryDoc.exists()) {
        return null;
      }
      
      const data = categoryDoc.data();
      const category: Category = {
        id: categoryDoc.id,
        name: data.name || '',
        type: data.type || 'expense',
        isActive: data.isActive !== false,
        sortOrder: data.sortOrder || 0,
        createdAt: data.createdAt || Date.now(),
        updatedAt: data.updatedAt || Date.now(),
      };
      
      return category;
    } catch (error) {
      console.error('❌ getCategory 실패:', error);
      throw error;
    }
  }

  /**
   * 새 카테고리를 생성합니다
   * @param userId 사용자 ID
   * @param categoryData 카테고리 데이터
   * @returns 생성된 카테고리 ID
   */
  async createCategory(userId: string, categoryData: CategoryCreateData, isPremium: boolean = false): Promise<string> {
    try {
      
      // 프리미엄 사용자는 sharedBooks에, 일반 사용자는 users에 카테고리 생성
      const categoriesRef = isPremium 
        ? collection(db, COLLECTIONS.SHARED_BOOKS, userId, COLLECTIONS.CATEGORIES)
        : collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.CATEGORIES);
      
      // 현재 시간
      const now = Date.now();
      
      // 새 카테고리 데이터
      const newCategoryData = {
        name: categoryData.name,
        type: categoryData.type,
        isActive: categoryData.isActive !== false, // 기본값 true
        sortOrder: categoryData.sortOrder || 0,
        createdAt: now,
        updatedAt: now,
      };
      
      const docRef = await addDoc(categoriesRef, newCategoryData);
      
      return docRef.id;
    } catch (error) {
      console.error('❌ createCategory 실패:', error);
      throw error;
    }
  }

  /**
   * 카테고리를 수정합니다
   * @param userId 사용자 ID
   * @param categoryId 카테고리 ID
   * @param updateData 수정할 데이터
   */
  async updateCategory(userId: string, categoryId: string, updateData: CategoryUpdateData): Promise<void> {
    try {
      
      const categoryRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.CATEGORIES, categoryId);
      
      // 업데이트 데이터에 updatedAt 추가
      const dataToUpdate = {
        ...updateData,
        updatedAt: Date.now(),
      };
      
      await updateDoc(categoryRef, dataToUpdate);
      
    } catch (error) {
      console.error('❌ updateCategory 실패:', error);
      throw error;
    }
  }

  /**
   * 카테고리를 삭제합니다
   * @param userId 사용자 ID
   * @param categoryId 카테고리 ID
   */
  async deleteCategory(userId: string, categoryId: string): Promise<void> {
    try {
      
      const categoryRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.CATEGORIES, categoryId);
      await deleteDoc(categoryRef);
      
    } catch (error) {
      console.error('❌ deleteCategory 실패:', error);
      throw error;
    }
  }

  /**
   * 카테고리 통계를 가져옵니다
   * @param userId 사용자 ID
   * @returns 카테고리 통계
   */
  async getCategoryStats(userId: string): Promise<{
    totalCategories: number;
    activeCategories: number;
    expenseCategories: number;
    incomeCategories: number;
  }> {
    try {
      
      const categories = await this.getUserCategories(userId);
      
      const stats = {
        totalCategories: categories.length,
        activeCategories: categories.filter(c => c.isActive).length,
        expenseCategories: categories.filter(c => c.type === 'expense').length,
        incomeCategories: categories.filter(c => c.type === 'income').length,
      };
      
      return stats;
    } catch (error) {
      console.error('❌ getCategoryStats 실패:', error);
      throw error;
    }
  }

  /**
   * SharedBooks에서 사용자의 거래처 목록을 가져옵니다
   * @param userId 사용자 ID
   * @returns 거래처 목록
   */
  async getMerchantsByUserFromShared(userId: string): Promise<Merchant[]> {
    try {
      
      const merchantsRef = collection(db, COLLECTIONS.SHARED_BOOKS, userId, 'merchant');
      const q = query(merchantsRef);
      
      const querySnapshot = await getDocs(q);
      const merchants: Merchant[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();

        // 웹 구버전이 id 필드 없이 저장한 문서 자동 복구 — Flutter 는 data.id 가
        // 없으면 목록 전체 파싱이 실패한다. 쓰기 권한 없는 멤버 조회는 조용히 무시.
        if (typeof data.id !== 'string' || data.id.length === 0) {
          updateDoc(doc.ref, { id: doc.id }).catch(() => {});
        }

        merchants.push({
          id: doc.id,
          name: data.companyName || data.name || '',
          description: data.representative || data.description || '',
          isActive: data.isActive !== false,
          createdAt: data.createdAt?.toMillis() || Date.now(),
          updatedAt: data.updatedAt?.toMillis() || Date.now(),
          // Firebase 실제 필드들
          companyName: data.companyName || '',
          phoneNumber: data.phoneNumber || '',
          representative: data.representative || '',
          address: data.address || '',
          category: data.category || '',
          accountNumber: data.accountNumber || '',
        });
      });


      return merchants;
    } catch (error) {
      console.error('❌ getMerchantsByUserFromShared 실패:', error);
      throw error;
    }
  }

  /**
   * 거래처 실시간 스냅샷을 설정합니다
   * @param userId 사용자 ID
   * @param callback 콜백 함수
   * @param options 쿼리 옵션
   * @returns 구독 해제 함수
   */
  onMerchantsSnapshot(
    userId: string,
    callback: (merchants: Merchant[]) => void,
    options?: QueryOptions,
    onError?: (error: Error) => void
  ): Unsubscribe {
    try {
      
      const merchantsRef = collection(db, COLLECTIONS.SHARED_BOOKS, userId, 'merchant');
      let q = query(merchantsRef);
      
      if (options?.limit) {
        q = query(q, limit(options.limit));
      }
      
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const merchants: Merchant[] = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();

          // 웹 구버전이 id 필드 없이 저장한 문서 자동 복구 (위 getMerchantsByUserFromShared 와 동일)
          if (typeof data.id !== 'string' || data.id.length === 0) {
            updateDoc(doc.ref, { id: doc.id }).catch(() => {});
          }

          merchants.push({
            id: doc.id,
            name: data.companyName || data.name || '',
            description: data.representative || data.description || '',
            isActive: data.isActive !== false,
            createdAt: data.createdAt?.toMillis() || Date.now(),
            updatedAt: data.updatedAt?.toMillis() || Date.now(),
            // Firebase 실제 필드들
            companyName: data.companyName || '',
            phoneNumber: data.phoneNumber || '',
            representative: data.representative || '',
            address: data.address || '',
            category: data.category || '',
            accountNumber: data.accountNumber || '',
          });
        });
        
        
        callback(merchants);
      }, (error) => {
        console.error('❌ onMerchantsSnapshot 에러:', error);
        onError?.(error as Error);
      });

      return unsubscribe;
    } catch (error) {
      console.error('❌ onMerchantsSnapshot 설정 실패:', error);
      throw error;
    }
  }

  /**
   * 거래처를 추가한다 (호스트 전용 — 규칙이 비호스트 쓰기를 거부)
   * @param bookId 공유가계부 ID (호스트 본인이면 자신의 uid)
   * @returns 생성된 문서 ID
   */
  async addMerchant(bookId: string, data: MerchantFormData): Promise<string> {
    const ref = collection(db, COLLECTIONS.SHARED_BOOKS, bookId, 'merchant');
    // Flutter Merchant.fromJson 은 문서 데이터의 id 필드를 필수(non-null)로 읽고,
    // 한 문서라도 파싱 실패하면 앱의 거래처 목록 전체가 드랍된다 → id 를 반드시 포함해 저장
    const docRef = doc(ref);
    await setDoc(docRef, {
      id: docRef.id,
      companyName: data.companyName,
      representative: data.representative ?? '',
      phoneNumber: data.phoneNumber ?? '',
      accountNumber: data.accountNumber ?? '',
      address: data.address ?? '',
      category: data.category ?? '',
      isActive: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  }

  /** 거래처를 수정한다 (호스트 전용) */
  async updateMerchant(bookId: string, merchantId: string, data: Partial<MerchantFormData>): Promise<void> {
    const ref = doc(db, COLLECTIONS.SHARED_BOOKS, bookId, 'merchant', merchantId);
    await updateDoc(ref, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  }

  /** 거래처를 삭제한다 (호스트 전용) */
  async deleteMerchant(bookId: string, merchantId: string): Promise<void> {
    const ref = doc(db, COLLECTIONS.SHARED_BOOKS, bookId, 'merchant', merchantId);
    await deleteDoc(ref);
  }

  /**
   * 현재 uid 가 접근 가능한 공유가계부 목록을 반환한다.
   * (1) 본인 호스트 book: hostId == uid
   * (2) 멤버 소속 book: members array-contains uid
   */
  async getAccessibleBooks(uid: string): Promise<AccessibleBook[]> {
    const ref = collection(db, COLLECTIONS.SHARED_BOOKS);
    const [hostSnap, memberSnap] = await Promise.all([
      getDocs(query(ref, where('hostId', '==', uid))),
      getDocs(query(ref, where('members', 'array-contains', uid))),
    ]);
    const toRaw = (d: QueryDocumentSnapshot): RawBook => {
      const data = d.data() as Partial<SharedBookDocument>;
      return { id: d.id, name: data.bookName, hostId: (data.hostId as string) ?? d.id };
    };
    return mergeAccessibleBooks(uid, hostSnap.docs.map(toRaw), memberSnap.docs.map(toRaw));
  }
}

// Firestore 서비스 인스턴스 생성 및 export
export const firestoreService = new FirestoreService();
export default firestoreService;

// 통합 데이터 로딩 메서드들을 static으로 export
export const loadUserData = (userId: string) => firestoreService.loadUserData(userId);
export const loadDashboardData = (userId: string, year: number, accountNumber?: number) => 
  firestoreService.loadDashboardData(userId, year, accountNumber);

// 디버깅 메서드들
export const debugSharedBooksStructure = (userId: string) => firestoreService.debugSharedBooksStructure(userId);
export const debugUserSubscription = (userId: string) => firestoreService.debugUserSubscription(userId);

// SharedBooks 관련 메서드들을 static으로 export
export const loadSharedBookData = (userId: string) => firestoreService.loadSharedBookData(userId);
export const loadPersonalData = (userId: string) => firestoreService.loadPersonalData(userId);
export const getSharedStores = (userId: string, accountNumber?: number) => firestoreService.getSharedStores(userId, accountNumber);
export const getSharedStoreByAccountNumber = (userId: string, accountNumber: number) => firestoreService.getSharedStoreByAccountNumber(userId, accountNumber);
export const addSharedExpense = (userId: string, expenseData: Omit<RecordDocument, 'id' | 'createdAt' | 'updatedAt'>) => 
  firestoreService.addSharedExpense(userId, expenseData);
export const updateSharedExpense = (userId: string, expenseId: string, updateData: Partial<RecordDocument>) => 
  firestoreService.updateSharedExpense(userId, expenseId, updateData);
export const deleteSharedExpense = (userId: string, expenseId: string) => 
  firestoreService.deleteSharedExpense(userId, expenseId);
export const onSharedExpensesSnapshot = (
  userId: string, 
  callback: (expenses: RecordDocument[]) => void, 
  options?: QueryOptions
) => firestoreService.onSharedExpensesSnapshot(userId, callback, options);
export const getExpensesByUserFromShared = (userId: string) => firestoreService.getExpensesByUserFromShared(userId);
export const getCategoriesByUserFromShared = (userId: string) => firestoreService.getCategoriesByUserFromShared(userId);

// 거래처 관련 메서드들을 static으로 export
export const getMerchantsByUserFromShared = (userId: string) => firestoreService.getMerchantsByUserFromShared(userId);
export const onMerchantsSnapshot = (
  userId: string,
  callback: (merchants: Merchant[]) => void,
  options?: QueryOptions,
  onError?: (error: Error) => void
) => firestoreService.onMerchantsSnapshot(userId, callback, options, onError);
export const addMerchant = (bookId: string, data: MerchantFormData) => firestoreService.addMerchant(bookId, data);
export const updateMerchant = (bookId: string, merchantId: string, data: Partial<MerchantFormData>) =>
  firestoreService.updateMerchant(bookId, merchantId, data);
export const deleteMerchant = (bookId: string, merchantId: string) => firestoreService.deleteMerchant(bookId, merchantId);
export const getAccessibleBooks = (uid: string) => firestoreService.getAccessibleBooks(uid);

// 직원 관리 관련 메서드들을 static으로 export
export const getEmployeesByUserFromShared = (userId: string, accountNumber?: number) => 
  firestoreService.getEmployeesByUserFromShared(userId, accountNumber);
export const getWorkSchedulesByUserFromShared = (userId: string, selectedDate?: Date, accountNumber?: number) => 
  firestoreService.getWorkSchedulesByUserFromShared(userId, selectedDate, accountNumber);
export const addEmployeeToShared = (userId: string, employeeData: any) => 
  firestoreService.addEmployeeToShared(userId, employeeData);
export const updateEmployeeInShared = (userId: string, employeeId: string, updateData: any) => 
  firestoreService.updateEmployeeInShared(userId, employeeId, updateData);
export const deleteEmployeeFromShared = (userId: string, employeeId: string) => 
  firestoreService.deleteEmployeeFromShared(userId, employeeId);
export const addWorkScheduleToShared = (userId: string, scheduleId: string, scheduleData: any) => 
  firestoreService.addWorkScheduleToShared(userId, scheduleId, scheduleData);
export const updateWorkScheduleInShared = (userId: string, scheduleId: string, updateData: any) => 
  firestoreService.updateWorkScheduleInShared(userId, scheduleId, updateData);
export const deleteWorkScheduleFromShared = (userId: string, scheduleId: string) => 
  firestoreService.deleteWorkScheduleFromShared(userId, scheduleId);
export const detachEmployeeFromSchedules = (
  userId: string,
  employeeId: string,
  accountNumber: number,
  employeeName: string,
) => firestoreService.detachEmployeeFromSchedules(userId, employeeId, accountNumber, employeeName);
export const deleteWorkSchedulesByEmployee = (
  userId: string,
  employeeId: string,
  accountNumber: number,
) => firestoreService.deleteWorkSchedulesByEmployee(userId, employeeId, accountNumber);
export const onEmployeesSnapshot = (
  userId: string, 
  callback: (employees: any[]) => void, 
  options?: QueryOptions
) => firestoreService.onEmployeesSnapshot(userId, callback, options);
export const onWorkSchedulesSnapshot = (
  userId: string, 
  callback: (schedules: any[]) => void, 
  options?: QueryOptions
) => firestoreService.onWorkSchedulesSnapshot(userId, callback, options);
